import { Controller } from "@hotwired/stimulus";
import { batch, computed, effect } from "@preact/signals-core";
import consumer from "../channels/consumer";
import { type Entry, type History, clipboardKey, historyFor, historyKey, historyShortcuts, idsOf, selectAllKey } from "../live/history";
import { createOutbox, readOnlyOutbox } from "../live/outbox";
import { createPresence } from "../live/presence";
import { NOTE_LIMIT, nextMark } from "../maps/notes";
import { notesBridge } from "../maps/notes_bridge";
import { notesHiddenStored, storeNotesHidden } from "../maps/notes_hidden";
import { openTable } from "../maps/open_table";
import { watchSaving } from "../live/saving";
import { createSceneState } from "../live/state";
import { createSync } from "../live/sync";
import { uuid } from "../live/uuid";
import {
	clampShift,
	type Drawing,
	type DrawSize,
	type DrawStyle,
	defaultPreset,
	fitOnScene,
	flipPoints,
	hitDrawing,
	isStyled,
	type LabelSize,
	type Look,
	labelSize,
	nearestSnap,
	PRESETS,
	presetStyle,
	simplify,
	translate,
} from "../maps/drawings";
import {
	applyEdgeGesture,
	dragEdges,
	type EdgeMode,
	type Edges,
	edgeId,
	edgePaintable,
	edgesOf,
	gestureMode,
	type Mark,
	menuMarkAt,
	nearestSide,
} from "../maps/edges";
import { geometryWrite, sceneTerrain } from "../maps/geometry";
import { centroid, freeSpot } from "../maps/combat_place";
import { initialTicks, toggled } from "../maps/combat_bridge";
import { cellsInRect, type FogState, fogRows, normalizeRows, paintFog, sameCells } from "../maps/fog";
import { gridMath } from "../maps/grid";
import type { createRenderer } from "../maps/renderer";
import { knownShape, parseWidth, type RulerShape } from "../maps/areas";
import { createRemoteRulers, createRulerFeed, endsInFog, type RulerKind, type RulerPoints } from "../maps/rulers";
import { layCell, STRAIGHT, setBorders, type Terrain, terrainOf } from "../maps/cuts";
import { applyCells, type CellChange, DIAGONAL, diagonalChange } from "../maps/diagonal";
import { paintTouches, type Touch } from "../maps/half_paint";
import { EMPTY, paintable, paintCells } from "../maps/terrain";
import { fillCells, rectCells } from "../maps/terrain_tools";
import {
	byBandThenZ,
	canMove,
	canTurn,
	groupDropTargets,
	hasMenu,
	hasOwnToken,
	isKeyMovable,
	isOwn,
	isVisible,
	keyStep,
	layerWrites,
	snapPoint,
	snapTarget,
	stepGroup,
	stepToken,
	tokenBand,
	tokenDiameter,
	turn,
	Z_LIMIT,
} from "../maps/tokens";
import {
	actionOf,
	type Change,
	createBody,
	type Direction,
	mergeChanges,
	recordKey,
	recordKeys,
	reverseAll,
	sceneChange,
	shapeKey,
	updateChange,
	type Write,
} from "../maps/undo";
import { tableCentre, tableViewOf } from "../maps/viewport";
import type { LiveNote, LiveToken, LiveTokenPreview } from "../types/generated/live";
import type MapDice from "./map_dice_controller";
import type MapDrawingMenu from "./map_drawing_menu_controller";
import type MapEdgeMenu from "./map_edge_menu_controller";
import type MapLabel from "./map_label_controller";
import type MapTokenMenu from "./map_token_menu_controller";

const TOAST_MS = 6000;
// Long enough to reach its link, and never dismissed while a mouse rests on
// it or the keyboard is on its link.
const LINK_TOAST_MS = 12_000;
const RULER_FOG_NOTICED = "maps-ruler-fog-noticed";
const RULER_SHAPE = "maps-ruler-shape";
const TOASTS_KEPT = 4;
const STATUS_DOTS = {
	live: "bg-verde-500",
	syncing: "bg-dourado-500 animate-pulse",
	saving: "bg-dourado-500 animate-pulse",
	offline: "bg-carmesim-500",
	rejected: "bg-carmesim-500",
};
// Only these reach screen readers; a syncing blip between two of them does not.
const ANNOUNCED = new Set(["live", "offline", "rejected"]);
const VIEWPORT_SAVE_MS = 400;
// Arrow keys, Q and E draw each step at once and write the last one after this
// pause.
const STEP_WRITE_MS = 400;
const SUBJECT_KEYS = ["character_id", "retainer_id", "monster_id", "prefab"];
// A tray item about to become a token: a subject's preview, or a marker or place the master names.
type TraySubject = Partial<LiveTokenPreview> & Partial<Pick<LiveToken, "label" | "prefab" | "ring_color">>;
// A freehand stroke keeps the points further than this share of a cell side
// from the line through its neighbours.
const SIMPLIFY_CELL = 0.02;
// MapDrawing::MAX_POINTS.
const MAX_DRAWING_POINTS = 1000;
// Screen pixels: a tap this close to the last vertex ends the shape, and the
// eraser reaches this far beyond a line's edge.
const DRAW_REACH_PX = 12;
// Points that differ by less than this are the same, to the pen's rounding.
const RESHAPE_EPSILON = 0.05;
// An arrow key nudges the selected drawing this share of the shorter cell side.
const NUDGE_CELL = 0.1;

type Plural = { one: string; other: string };
type DrawMode = "free" | "segments" | "area" | "text";
type DrawChoices = {
	mode: DrawMode;
	preset: string;
	color: string;
	size: DrawSize;
	style: DrawStyle;
	label: LabelSize;
	eraser: boolean;
	move: boolean;
	snap: boolean;
};
// The new label the editor is open on.
export type LabelDraft = Look & { points: number[]; text: string };
type DrawGesture = { phase: "start" | "move" | "end" | "cancel" | "hover" | "menu"; point: { x: number; y: number } | null; tap: boolean };
type FogChoices = { mode: "reveal" | "hide"; shape: "rectangle" | "brush" };
type Point = { x: number; y: number };
type Cell = { col: number; row: number };
// A stroke's cell with the points of the stroke that fell in it.
type StrokeCell = Cell & { x?: number; y?: number; points?: { x: number; y: number }[] };
// A point as fractions 0..1 of the cell from its north-west corner.
const fractionIn = (math: ReturnType<typeof gridMath>, { col, row }: Cell, { x, y }: Point) => {
	const centre = math.centerOf(col, row);
	return { x: (x - centre.x) / math.grid.cell_w + 0.5, y: (y - centre.y) / math.grid.cell_h + 0.5 };
};
// The middle 60% of a cell on each axis, where a swipe touches it.
const INNER = ({ x, y }: { x: number; y: number }) => [x, y].every((fraction) => fraction >= 0.2 - 1e-9 && fraction <= 0.8 + 1e-9);
type FogGesture = { phase: "start" | "move" | "end" | "cancel"; from: Point | null; to: Point | null; cells: Cell[] };
type EdgeGesture = { phase: "start" | "move" | "end" | "cancel"; from: Point | null; to: Point | null };
// What the keys changed on a token and have not written yet.
type StepDraft = { id: string; x?: number; y?: number; rotation?: number };
// A handle drag on a piece, as the renderer reports it.
type TransformChange = {
	phase: "start" | "move" | "end" | "cancel";
	id: string;
	size?: number;
	rotation?: number;
	span?: number | null;
	x?: number;
	y?: number;
	// A drawing's points, and on release what made them.
	points?: number[];
	gesture?: Gesture;
};
// `record: false` for undo's own writes; `merge` makes one entry of consecutive writes.
type WriteOptions = { record?: boolean; quiet?: boolean; merge?: string; gesture?: Gesture };
// What made a points write, which names its step.
type Gesture = "move" | "turn" | "resize" | "flip" | "front" | "back";
// A drawing's write is one of these, never two.
type DrawingWrite = { text: string } | { points: number[] } | { color: string } | { width: number } | { style: DrawStyle };
type Rejection = { mutationId: string; reason: string | null; quiet?: boolean; dropped?: string[] };
type Request = { method: string; url: string; body?: object; ops: object[] };
// What map_scenes/_board.html.erb passes from the locale.
type Labels = {
	measure: { format: string; circle: string; cone: string; line: string; square: Plural; hex: Plural };
	rejected: string;
	status: Record<string, string>;
	statusDetail: Record<string, string>;
	tableSent: string;
	refused: Record<string, string>;
	rulerInFog: string;
	multi: string;
	toggled: Record<string, string>;
	history: { undo: string; redo: string; undo_action: string; redo_action: string; piece: string; actions: Record<string, string> };
};

// Connects to data-controller="map-board"
// Owns the scene's state, outbox, sync and canvas. The root is
// data-turbo-permanent, so Turbo moving it disconnects and reconnects this
// controller: the board is only torn down once the element has really left the
// page. A permanent element Turbo carries back keeps its old inline snapshot, so
// every connect also resyncs. The tray and the token menu are controllers of
// their own on the same root; they write through this one.
export default class extends Controller<HTMLElement> {
	static targets = [
		"snapshot",
		"canvas",
		"ruler",
		"rulerShape",
		"rulerWidthField",
		"rulerWidth",
		"rulerUnit",
		"measure",
		"toasts",
		"toastTemplate",
		"status",
		"statusDot",
		"statusLabel",
		"statusDetail",
		"announcer",
		"name",
		"frozen",
		"frozenNotice",
		"noTokenNote",
		"activate",
		"activeNotice",
		"sendTable",
		"undo",
		"redo",
		"zoomPill",
		"zoomLevel",
	];
	static values = {
		snapshotUrl: String,
		scenesUrl: String,
		tokensUrl: String,
		drawingsUrl: String,
		notesUrl: String,
		campaignId: String,
		accountId: String,
		manage: Boolean,
		table: Boolean,
		tableUrl: String,
		channel: Object,
		readOnly: Boolean,
		labels: Object,
		tiles: Object,
		monoTiles: Object,
		rock: String,
		frameless: Array,
		stickers: Array,
		monoPlaces: Object,
		unlabeledTitles: Object,
		rulerHelpUrl: String,
	};
	declare readonly snapshotTarget: HTMLScriptElement;
	declare readonly canvasTarget: HTMLElement;
	declare readonly rulerTargets: HTMLElement[];
	declare readonly rulerShapeTargets: HTMLElement[];
	declare readonly rulerWidthFieldTargets: HTMLElement[];
	declare readonly rulerWidthTargets: HTMLInputElement[];
	declare readonly rulerUnitTargets: HTMLElement[];
	declare readonly measureTarget: HTMLElement;
	declare readonly toastsTarget: HTMLElement;
	declare readonly toastTemplateTarget: HTMLTemplateElement;
	declare readonly toastTemplateTargets: HTMLTemplateElement[];
	declare readonly statusTarget: HTMLElement;
	declare readonly hasStatusTarget: boolean;
	declare readonly statusDotTarget: HTMLElement;
	declare readonly statusLabelTarget: HTMLElement;
	declare readonly statusDetailTarget: HTMLElement;
	declare readonly announcerTarget: HTMLElement;
	declare readonly nameTargets: HTMLElement[];
	declare readonly frozenTargets: HTMLElement[];
	declare readonly frozenNoticeTargets: HTMLElement[];
	declare readonly noTokenNoteTargets: HTMLElement[];
	declare readonly activateTargets: HTMLElement[];
	declare readonly activeNoticeTargets: HTMLElement[];
	declare readonly sendTableTargets: HTMLElement[];
	declare readonly undoTargets: HTMLButtonElement[];
	declare readonly redoTargets: HTMLButtonElement[];
	declare readonly zoomPillTargets: HTMLElement[];
	declare readonly zoomLevelTargets: HTMLElement[];
	declare readonly snapshotUrlValue: string;
	declare readonly scenesUrlValue: string;
	declare readonly tokensUrlValue: string;
	declare readonly drawingsUrlValue: string;
	declare readonly notesUrlValue: string;
	declare readonly campaignIdValue: string;
	declare readonly accountIdValue: string;
	declare readonly manageValue: boolean;
	declare readonly tableValue: boolean;
	declare readonly tableUrlValue: string;
	declare readonly channelValue: object;
	declare readonly readOnlyValue: boolean;
	declare readonly labelsValue: Labels;
	declare readonly tilesValue: Record<string, string>;
	declare readonly monoTilesValue: Record<string, string>;
	declare readonly rockValue: string;
	declare readonly framelessValue: string[];
	declare readonly stickersValue: string[];
	declare readonly monoPlacesValue: Record<string, string>;
	declare readonly unlabeledTitlesValue: Record<string, string>;
	declare readonly rulerHelpUrlValue: string;
	state: ReturnType<typeof createSceneState>;
	outbox: ReturnType<typeof createOutbox> | ReturnType<typeof readOnlyOutbox>;
	sync: ReturnType<typeof createSync>;
	renderer: ReturnType<typeof createRenderer>;
	// Kept apart from the renderer, which is created after the tray may already ask for it.
	floorGrid = false;
	rulerFeed?: ReturnType<typeof createRulerFeed>;
	remoteRulers?: ReturnType<typeof createRemoteRulers>;
	subscription?: { perform(action: string, data: object): void };
	// Who has a board of the campaign open; none on the table view or the link.
	presence?: ReturnType<typeof createPresence>;
	presenceClient?: string;
	// This tab's undo and redo for the scene; none on the table view or the link.
	history?: History<Change> | null;
	// The record or scene field each write of the history's still out
	// touches, by mutation id, whether or not its entry is still on a stack.
	outstanding?: Map<string, string>;
	removals?: Map<string, Extract<Change, { record: unknown }>>;
	// The writes made inside `group()`, which become one step of the history.
	grouped?: { mutationId: string; step: { label: string; change: Change; merge?: string } }[] | null;
	// A press of Desfazer or Refazer that waits for the answer of the removal it would take back (`mutationId`).
	queuedTravel?: { direction: Direction; mutationId: string } | null;
	// The pieces last copied (their records), and how many pastes since, which stagger the pastes made with no pointer on the board.
	clipboard?: { pieces: LiveToken[]; pastes: number } | null;
	onVisibility?: () => void;
	teardown?: ReturnType<typeof setTimeout>;
	destroyed = false;
	leaving = false;
	gone = false;
	dropped = false;
	announced?: string;
	savingWatch?: ReturnType<typeof watchSaving>;
	openPanels: Set<Element>;
	panelObserver: ResizeObserver;
	toastTimers?: Set<ReturnType<typeof setTimeout>>;
	rulerFogNoticed = false;
	rulerShape: RulerShape = "ruler";
	rulerWidth: number | null = null;
	followedScene?: string;
	placing?: (() => Record<string, unknown>) | null;
	brush?: string | null;
	paintTool: "brush" | "rectangle" | "fill" = "brush";
	paintBorder = STRAIGHT;
	rectKey?: string | null;
	stroke?: Map<string, Touch> | null;
	diagonal?: {
		turned: Map<string, { cell: Cell; change: CellChange }>;
		origin: { key: string; cell: Cell; pointer: { x: number; y: number }; left: boolean } | null;
		work: Terrain | null;
		basis: unknown;
	} | null;
	drawArmed?: boolean;
	noteArmed?: boolean;
	fogArmed?: boolean;
	fogChoice?: FogChoices;
	fogStroke?: Map<string, Cell> | null;
	edgeKind?: string | null;
	// The edges a gesture has taken, and whether it paints or removes, which
	// its first edge decides; a tap that becomes a drag starts again.
	edgeStroke?: { taken: Map<string, Mark>; last: Point; tap: boolean; mode?: EdgeMode; basis?: unknown } | null;
	drawChoice?: DrawChoices;
	lineMode?: DrawMode;
	label?: LabelDraft | null;
	// The vertices, or the freehand points, of the shape being drawn.
	shape?: number[] | null;
	// The drawing a press under *Mover desenho* took hold of, and where.
	// `again` when it was already selected, `badge` when the press took its "⋯".
	grab?: { id: string; from: { x: number; y: number }; again?: boolean; badge?: boolean } | null;
	stepTimer?: ReturnType<typeof setTimeout>;
	moveTimer?: ReturnType<typeof setTimeout>;
	viewportTimer?: ReturnType<typeof setTimeout>;
	disposeMirror?: () => void;
	disposeStatus?: () => void;
	disposeScene?: () => void;
	disposeShape?: () => void;
	disposeSelection?: () => void;
	disposeNoteSelection?: () => void;
	disposeOwnToken?: () => void;
	disposeViewport?: () => void;
	disposeReveal?: () => void;
	disposeMove?: () => void;
	// The drawing a handle drag started on and the geometry it started from.
	reshapeBase?: { id: string; points: number[]; width: number } | null;
	disposeReshape?: () => void;
	disposeHistory?: () => void;

	connect() {
		clearTimeout(this.teardown);
		if (this.state) this.sync?.resync();
		else this.boot();
	}

	// The label editor closes when Turbo moves the root; the label goes with
	// it, or the next tap would save a session nobody can see.
	disconnect() {
		this.closeLabel();
		this.teardown = setTimeout(() => {
			if (!this.element.isConnected) this.destroy();
		});
	}

	// The table view is the players' view on a screen of its own: nothing
	// there is anyone's own, and every ping, the master's too, shows.
	get viewer() {
		return { manage: this.manageValue, accountId: this.tableValue ? null : this.accountIdValue, table: this.tableValue };
	}

	async boot() {
		const snapshot = JSON.parse(this.snapshotTarget.textContent);
		this.state = createSceneState(snapshot);
		this.history = this.tableValue || this.readOnlyValue || !snapshot.scene?.id ? null : historyFor<Change>(snapshot.scene.id);
		this.outbox = this.readOnlyValue
			? readOnlyOutbox()
			: createOutbox({
					state: this.state,
					onReject: (rejection) => this.rejected(rejection),
					onSettle: (settle) => this.settled(settle),
				});
		this.startPresence();
		this.sync = createSync({
			state: this.state,
			outbox: this.outbox,
			// The scene id keeps the identifier apart from another subscription to the
			// campaign's stream still closing, which the server would take as a duplicate.
			subscribe: (callbacks) => this.subscribe(callbacks, snapshot.scene?.id),
			fetchSnapshot: ({ signal }) => this.fetchSnapshot(signal),
			onMessage: (message) => this.received(message),
			onGone: () => {
				this.gone = true;
				this.visit(this.tableValue ? this.tableUrlValue : this.scenesUrlValue);
			},
		});
		if (!this.tableValue) this.outbox.restore();
		this.disposeMirror = effect(() => {
			this.element.dataset.liveVersion = String(this.state.version.value);
			this.element.dataset.sceneId = this.state.view.value.scene?.id ?? "";
		});
		this.savingWatch = watchSaving({ pending: this.state.pending });
		this.disposeStatus = effect(() => this.showStatus(this.sync.status.value, this.savingWatch.saving.value));
		this.disposeScene = effect(() => this.followScene(this.state.view.value.scene));
		this.disposeShape = effect(() => this.keepHistoryShape());
		this.disposeSelection = effect(() => this.keepVisibleSelection(this.state.view.value.tokens ?? {}));
		this.disposeOwnToken = effect(() => this.updateOwnTokenNote(this.state.view.value.tokens ?? {}));
		if (this.manageValue && !this.tableValue) this.startNotes();
		if (this.history) this.disposeHistory = effect(() => this.showHistory());
		document.addEventListener("turbo:fetch-request-error", this.visitFailed);
		if (this.presence) {
			this.onVisibility = () => this.visibilityChanged();
			document.addEventListener("visibilitychange", this.onVisibility);
		}

		const { createRenderer } = await import("../maps/renderer");
		if (this.destroyed) return;
		this.canvasTarget.replaceChildren();
		this.rulerFeed = createRulerFeed({ send: (points, kind) => this.sendRuler(points, kind) });
		this.remoteRulers = createRemoteRulers({
			show: (key, points, color, kind) => this.renderer?.showRuler(key, points, color, kind),
			hide: (key) => this.renderer?.hideRuler(key),
		});
		this.renderer = createRenderer({
			container: this.canvasTarget,
			state: this.state,
			viewer: this.viewer,
			measureLabel: (measurement) => this.measureLabel(measurement),
			onMeasure: (measurement) => this.showMeasure(measurement),
			onRuler: (points, kind) => (points ? this.rulerFeed.update(points, kind) : this.rulerFeed.end()),
			onTap: (tap) => this.tap(tap),
			onBox: (rect) => this.selectBox(rect),
			onTokenMove: (id, point) => this.moveToken(id, point),
			onTokensMove: (moves) => this.moveTokens(moves),
			onTokenMenu: (id) => this.openMenu(id),
			onNoteTap: (id) => this.selectNote(id),
			onNoteMove: (id, point) => this.moveNote(id, point),
			onPing: (point) => this.ping(point),
			onTransform: (change) => this.transform(change),
			onPaint: (stroke) => this.paint(stroke),
			onFog: (gesture) => this.fogGesture(gesture),
			onEdge: (gesture) => this.edge(gesture),
			onDraw: (gesture) => this.drawGesture(gesture),
			tiles: this.tilesValue,
			monoTiles: this.monoTilesValue,
			rock: this.rockValue,
			frameless: this.framelessValue,
			monoPlaces: this.monoPlacesValue,
			interactive: !this.tableValue,
			follow: this.tableValue ? (size) => tableCentre(this.state.view.peek().scene?.table_view, size) : null,
			insets: () => this.chromeInsets(),
		});
		this.renderer.setFloorGrid(this.floorGrid);
		try {
			this.rulerShape = knownShape(localStorage.getItem(RULER_SHAPE));
		} catch {}
		this.applyRulerKind();
		this.openPanels = new Set();
		this.panelObserver = new ResizeObserver((entries) => this.panelsResized(entries));
		for (const panel of this.element.querySelectorAll('[data-map-inset="panel"], [data-map-inset="sheet"]')) this.panelObserver.observe(panel);
		this.disposeReveal = effect(() => {
			if (this.state.local.selection.value.length) this.keepSelectionVisible();
		});
		this.restoreViewport();
		this.disposeViewport = effect(() => {
			this.showZoom(this.state.local.viewport.value.zoom);
			this.rememberViewport();
		});
		this.exposeForTests();
		this.openArrivalNote();
		this.dispatch("ready");
	}

	// Experimentar agora lands on `?nota=1`: the master's note with that mark opens, once, and the address is left clean.
	openArrivalNote() {
		const url = new URL(location.href);
		const mark = url.searchParams.get("nota");
		if (mark === null) return;
		url.searchParams.delete("nota");
		history.replaceState(history.state, "", url);
		const note = this.manageValue && !this.tableValue ? this.notes.find((it) => it.mark === mark) : undefined;
		if (note) this.focusNote(note.id);
	}

	// An answer the state did not apply (a replay, or a version gap) stays pending until a snapshot fetched after it.
	settled({ mutationId, applied, payload }: { mutationId?: string; applied?: boolean; payload?: { ops?: unknown; participant?: Record<string, unknown> } }) {
		const removal = mutationId && this.removals?.get(mutationId);
		if (removal && Array.isArray(payload?.ops)) {
			if (payload.participant) removal.combat = payload.participant;
			else delete removal.combat;
		}
		if (mutationId) this.removals?.delete(mutationId);
		const queued = this.queuedTravel;
		if (queued && queued.mutationId === mutationId) {
			this.queuedTravel = null;
			const next = queued.direction === "undo" ? this.history?.nextUndo : this.history?.nextRedo;
			const entry = next?.peek();
			if (entry && idsOf(entry).includes(mutationId)) this.travel(queued.direction);
		}
		if (applied === false) this.sync?.resync();
	}

	// The answer to a write sent outside the outbox (a picture goes as multipart, with its own progress): its diff applies when it
	// is the next version, as the outbox applies one; an older one is the broadcast's own, already applied, and a gap resyncs.
	applyAnswer({ version, ops, mutation_id }: { version?: number; ops?: unknown; mutation_id?: string }) {
		const current = this.state.version.peek();
		if (version === current + 1 && Array.isArray(ops)) this.state.applyDiff({ version, ops, mutationId: mutation_id });
		else if (version !== undefined && version > current + 1) this.sync.resync();
	}

	// A refused write takes the whole history of what it touched, since a
	// step's before may be a value the server never had; an undo's own
	// refusal is quiet, as the guard would have been had it foreseen it.
	rejected({ mutationId, reason, quiet = false, dropped = [] }: Rejection) {
		const ids = [mutationId, ...dropped];
		const keys = new Set(ids.map((id) => this.outstanding?.get(id)).filter(Boolean));
		for (const id of ids) {
			this.outstanding?.delete(id);
			this.removals?.delete(id);
		}
		if (this.queuedTravel && ids.includes(this.queuedTravel.mutationId)) this.queuedTravel = null;
		this.history?.forget(ids, (change) => recordKeys(change).some((key) => keys.has(key)));
		if (!quiet) this.toast(reason || this.labelsValue.rejected);
	}

	// The history outlives the board, but a refusal reaches only a live one:
	// a write still out when the board goes takes its record's steps along.
	forgetOutstanding() {
		const pending = new Set(this.state?.pending.peek().map((entry: { mutationId: string }) => entry.mutationId));
		const keys = new Set([...(this.outstanding ?? [])].filter(([id]) => pending.has(id)).map(([, key]) => key));
		if (keys.size) this.history?.forget([], (change) => recordKeys(change).some((key) => keys.has(key)));
	}

	// A token's removal may answer the combatant it took with it, which the write that brings the token back needs.
	// Whatever the change's own type is: undoing a placement removes it too.
	awaitCombat(mutationId: string, change: Extract<Change, { record: unknown }>) {
		this.removals ??= new Map();
		this.removals.set(mutationId, change);
	}

	// Kept outside the bounded stacks, and pruned of what has settled.
	track(mutationId: string, change: Change) {
		this.outstanding ??= new Map();
		const pending = new Set(this.state.pending.peek().map((entry: { mutationId: string }) => entry.mutationId));
		for (const id of this.outstanding.keys()) if (!pending.has(id)) this.outstanding.delete(id);
		this.outstanding.set(mutationId, recordKey(change));
	}

	startPresence() {
		if (this.tableValue) return;
		this.presenceClient = uuid();
		this.presence = createPresence({
			self: { accountId: this.accountIdValue, client: this.presenceClient },
			send: (data) => this.subscription?.perform("here", data),
		});
	}

	// The roster speaks once sync has its own connection, and falls silent with it.
	subscribe(callbacks, sceneId) {
		const { presence } = this;
		const params = { ...this.channelValue, scene_id: sceneId, ...(presence ? { presence: this.presenceClient } : {}) };
		const wrapped = presence
			? {
					...callbacks,
					connected: (...args) => {
						callbacks.connected(...args);
						presence.connected();
					},
					disconnected: (...args) => {
						callbacks.disconnected(...args);
						presence.disconnected();
					},
				}
			: callbacks;
		this.subscription = consumer.subscriptions.create(params, wrapped);
		return this.subscription;
	}

	visibilityChanged() {
		if (!document.hidden) return this.presence?.visible();
		this.presence?.hidden();
		if (this.renderer?.tool === "ruler") this.setRuler(false);
	}

	async fetchSnapshot(signal?: AbortSignal) {
		const response = await fetch(this.snapshotUrlValue, { headers: { Accept: "application/json" }, credentials: "same-origin", cache: "no-store", signal });
		if (!response.ok) throw Object.assign(new Error(`snapshot ${response.status}`), { status: response.status });
		return response.json();
	}

	// Pings and rulers for this scene, participants, presence and, on the players' stream,
	// `reload` when the table moves: a player goes to the list, which opens the
	// scene now on the table, and the table view and link to their own address.
	// Sync applies the shown scene's own diffs, which never reach here.
	// Participants are unversioned: a resync's snapshot replaces them.
	received(message) {
		const sceneId = this.state.view.peek().scene?.id;
		if (message?.type === "ping") {
			if (message.scene_id === sceneId && message.account_id !== this.viewer.accountId) {
				const name = this.tableValue ? undefined : (this.participant(message.account_id)?.name ?? undefined);
				this.renderer?.showPing({ x: message.x, y: message.y, color: message.color, accountId: message.account_id, name });
				if (message.account_id) this.dispatch("pinged", { detail: { accountId: message.account_id } });
			}
		} else if (message?.type === "ruler") {
			// The table link's rulers name no account, so its colour tells them apart.
			if (message.scene_id === sceneId && message.account_id !== this.viewer.accountId) {
				const { shape, width } = message;
				this.remoteRulers?.received({
					key: message.account_id ?? message.color ?? "",
					points: message.points ?? null,
					color: message.color ?? null,
					shape,
					width,
				});
			}
		} else if (message?.type === "music") {
			this.dispatch("music", { detail: message });
		} else if (message?.type === "effect") {
			this.dispatch("effect", { detail: message });
		} else if (message?.type === "presence") {
			this.presence?.received({ event: message.event, accountId: message.account_id, client: message.client });
		} else if (message?.type === "participant") {
			const { op, collection, id, attrs } = message;
			this.state.applyDiff({ ops: [{ op, collection, id, attrs }] });
		} else if (message?.type === "reload") {
			this.visit(this.tableValue ? this.tableUrlValue : this.scenesUrlValue);
		}
	}

	// Judged on the accepted scene, so an optimistic size that is refused costs nothing; a save's answer and a broadcast both land here.
	keepHistoryShape() {
		this.history?.keep(shapeKey((this.state.confirmed.value as { scene?: Parameters<typeof shapeKey>[0] }).scene));
	}

	// A player whose scene left the table, while they watched or were away,
	// goes to the list, which opens the active scene or waits for one. The
	// table view goes to its own address, which does the same without ever
	// showing the master's list.
	followScene(scene) {
		if (this.tableValue) {
			if (!scene?.active) this.visit(this.tableUrlValue);
			return;
		}
		if (!scene || (!this.manageValue && !scene.active)) {
			this.visit(this.scenesUrlValue);
			return;
		}
		if (this.followedScene && this.followedScene !== scene.id) this.sceneChanged();
		this.followedScene = scene.id;
		for (const unit of this.rulerUnitTargets) unit.textContent = scene.grid?.unit ?? "m";
		for (const name of this.nameTargets) name.textContent = scene.name;
		for (const button of this.frozenTargets) button.setAttribute("aria-pressed", String(Boolean(scene.movement_frozen)));
		for (const notice of this.frozenNoticeTargets) notice.hidden = !scene.movement_frozen;
		for (const button of this.activateTargets) button.hidden = Boolean(scene.active);
		for (const notice of this.activeNoticeTargets) notice.hidden = !scene.active;
		for (const button of this.sendTableTargets) button.hidden = !scene.active;
		if (this.fogArmed && !scene.fog?.enabled) this.setFog(false);
	}

	// A ruler resting on the old scene means nothing on the new one, here or on others' boards.
	sceneChanged() {
		if (this.renderer?.tool === "ruler") this.setRuler(false);
		this.remoteRulers?.clear();
	}

	// Leaving is the one navigation the board makes: its scene is gone, or the
	// table moved to another one. Sync itself never navigates.
	visit(url) {
		if (this.leaving) return;
		this.leaving = true;
		Turbo.visit(url, { action: "replace" });
	}

	// A failed visit lets the next change try again. Turbo follows one that
	// failed on the network with a full load of its address, which still leaves.
	visitFailed = () => {
		this.leaving = false;
	};

	// The badge shows a short word below sm (the board's only to screen
	// readers) and the full sentence above; the announcer speaks only when the
	// board drops or comes back, not on every syncing blip or slow save.
	showStatus(status, saving) {
		this.element.dataset.liveStatus = status;
		this.element.toggleAttribute("data-live-saving", saving);
		if (status === "offline") this.dropped = true;
		if (status === "live" && this.dropped) {
			this.dropped = false;
			this.dispatch("reconnected");
		}
		if (!this.hasStatusTarget) return;
		const shown = saving && (status === "live" || status === "syncing") ? "saving" : status;
		const { status: labels, statusDetail: details } = this.labelsValue;
		const detail = shown === "live" ? labels.live : (details[shown] ?? labels[shown]);
		this.statusTarget.dataset.status = shown;
		this.statusTarget.title = detail;
		this.statusDotTarget.className = `shrink-0 w-2.5 h-2.5 rounded-full ${STATUS_DOTS[shown]}`;
		this.statusLabelTarget.textContent = labels[shown];
		this.statusDetailTarget.textContent = detail;
		this.statusLabelTarget.classList.toggle("sr-only", shown === "live");
		this.statusDetailTarget.classList.toggle("sm:inline", shown !== "live");
		if (!ANNOUNCED.has(status) || status === this.announced) return;
		if (this.announced || status !== "live") this.announcerTarget.textContent = details[status];
		this.announced = status;
	}

	// Named for the step each would take, and titled with its keys too, ⌘ on
	// a Mac; `aria-keyshortcuts` names the keys for screen readers.
	showHistory() {
		const { history } = this;
		const labels = this.labelsValue.history;
		const platform = (navigator as Navigator & { userAgentData?: { platform: string } }).userAgentData?.platform ?? navigator.platform ?? "";
		const keys = historyShortcuts(/mac|iphone|ipad/i.test(platform));
		const buttons = [
			[this.undoTargets, history.nextUndo.value, labels.undo, labels.undo_action, keys.undo],
			[this.redoTargets, history.nextRedo.value, labels.redo, labels.redo_action, keys.redo],
		] as const;
		const view = this.manageValue ? null : this.state.view.value;
		for (const [targets, next, name, format, key] of buttons) {
			const can = Boolean(next) && !(view && this.waits(next, view));
			const label = can ? format.replace("%{action}", () => next.label) : name;
			for (const button of targets) {
				button.disabled = !can;
				button.title = `${label} (${key})`;
				button.setAttribute("aria-label", label);
			}
		}
	}

	// What the chrome covers, from each edge: the top bar, the toolbar, and a
	// panel docked on the right or, on a phone, across the bottom. The token
	// menu counts only as that bottom sheet, never as the popover beside a token.
	// Docking is measured from the safe-area frame, which a notch pushes inward.
	chromeInsets() {
		const board = this.element.getBoundingClientRect();
		const frame = this.element.querySelector(".odo-board-frame")?.getBoundingClientRect() ?? board;
		const insets = { top: 0, right: 0, bottom: 0, left: 0 };
		for (const element of this.element.querySelectorAll<HTMLElement>("[data-map-inset]")) {
			const rect = element.getBoundingClientRect();
			const side = element.dataset.mapInset;
			if (!rect.width || !rect.height) continue;
			if (side === "top") insets.top = Math.max(insets.top, rect.bottom - board.top);
			else if (side === "left") insets.left = Math.max(insets.left, rect.right - board.left);
			else if (rect.width > board.width * 0.9) insets.bottom = Math.max(insets.bottom, board.bottom - rect.top);
			else if (side === "panel" && frame.right - rect.right < 24) insets.right = Math.max(insets.right, board.right - rect.left);
		}
		return insets;
	}

	// Only a panel or sheet opening pans: the dice feed growing inside an open
	// one must not. A scene still fitting the board fits again around a docked
	// panel that opens or closes, never around the token menu's sheet, which
	// only ever pans.
	panelsResized(entries) {
		let opened = false;
		let refit = false;
		for (const { target, contentRect } of entries) {
			const shown = contentRect.width > 0 && contentRect.height > 0;
			if (shown !== this.openPanels.has(target) && (target as HTMLElement).dataset.mapInset === "panel") refit = true;
			if (shown && !this.openPanels.has(target)) opened = true;
			if (shown) this.openPanels.add(target);
			else this.openPanels.delete(target);
		}
		if (refit && this.renderer.autoFit) this.renderer.fit();
		if (this.zoomPillTargets.length) {
			const clear = `${this.chromeInsets().right}px`;
			for (const pill of this.zoomPillTargets) pill.style.marginRight = clear;
		}
		if (opened) this.keepSelectionVisible();
		if (refit && this.noTokenNoteTargets.length) this.updateOwnTokenNote(this.state.view.peek().tokens ?? {});
	}

	// A panel or sheet opening over the selected token, or a token selected
	// under one, pans the map, never zooms it, until the token shows beside it.
	keepSelectionVisible() {
		const id = this.state.local.selection.peek().at(-1);
		const token = id && this.state.view.peek().tokens?.[id];
		if (!token || !this.renderer) return;
		const { step, group } = this.state.local.drafts.peek();
		const at = step?.id === id ? { ...token, ...step } : { ...token, ...group?.[id] };
		const radius = (tokenDiameter(this.gridMath().grid, token.size ?? 1) / 2) * this.state.local.viewport.peek().zoom;
		this.renderer.reveal(at, radius + 12);
	}

	// A token the master hides leaves a player's selection, so a tap elsewhere
	// never moves what they can no longer see.
	keepVisibleSelection(tokens) {
		const selection = this.state.local.selection.peek();
		const kept = selection.filter((id) => tokens[id] && isVisible(tokens[id], this.viewer));
		if (kept.length !== selection.length) this.setSelection(kept);
	}

	// A player with no piece on the scene has nothing to act on; the note is
	// rendered for a player only, so this is a no-op elsewhere. It also steps
	// aside while a panel docks on the right (the dice drawer), which would
	// otherwise paint over it at some widths, both sharing z-20.
	updateOwnTokenNote(tokens) {
		if (!this.noTokenNoteTargets.length) return;
		const hidden = hasOwnToken(tokens, this.viewer) || this.chromeInsets().right > 0;
		for (const note of this.noTokenNoteTargets) note.hidden = hidden;
	}

	destroy() {
		this.destroyed = true;
		this.flushStep();
		this.flushMove();
		this.disposeMove?.();
		clearTimeout(this.viewportTimer);
		for (const timer of this.toastTimers ?? []) clearTimeout(timer);
		document.removeEventListener("turbo:fetch-request-error", this.visitFailed);
		this.rulerFeed?.end();
		this.remoteRulers?.clear();
		if (this.onVisibility) document.removeEventListener("visibilitychange", this.onVisibility);
		this.presence?.destroy();
		this.forgetOutstanding();
		this.outbox?.destroy();
		this.sync?.destroy();
		this.disposeMirror?.();
		this.disposeStatus?.();
		this.savingWatch?.dispose();
		this.disposeScene?.();
		this.disposeShape?.();
		this.disposeSelection?.();
		this.disposeNoteSelection?.();
		if (notesBridge.value && notesBridge.value.selected === this.state?.local.note) notesBridge.value = null;
		this.disposeOwnToken?.();
		this.disposeHistory?.();
		this.disposeViewport?.();
		this.disposeReveal?.();
		this.disposeReshape?.();
		this.panelObserver?.disconnect();
		this.renderer?.destroy();
		if (window.odMaps?.element === this.element) delete window.odMaps;
	}

	tap({ tokenId, point, shiftKey = false }) {
		const ticks = this.state.local.ticks?.peek();
		if (ticks) {
			if (tokenId) this.state.local.ticks.value = toggled(ticks, tokenId);
			return;
		}
		if (this.noteArmed) return this.createNote(point);
		if (this.placing) return this.placeToken(this.placing(), point);
		const view = this.state.view.peek();
		const token = tokenId ? view.tokens?.[tokenId] : null;
		if (token) return this.tapToken(token, view.scene, shiftKey);
		// Shift is a way to add to a selection: a slip of it onto the map keeps what is selected.
		if (shiftKey && this.manageValue && this.state.local.selection.peek().length) return;
		if (this.openEdgeMenu(point)) return;

		const [selectedId] = this.state.local.selection.peek();
		const selected = selectedId ? view.tokens?.[selectedId] : null;
		if (!this.manageValue && selected && canMove(selected, this.viewer, view.scene)) {
			this.moveToken(selected.id, snapTarget(this.gridMath(), selected, point));
		}
		this.selectNote(null);
		this.select(null);
	}

	// A first tap selects; the master's second tap opens the menu, and a
	// player's lets go, since a player's tap on the map moves what is selected.
	// The master's Shift+click, or a tap while *Selecionar várias* is on, adds a piece to the selection or takes it out instead.
	tapToken(token, scene, shiftKey = false) {
		const selection = this.state.local.selection.peek();
		const selected = selection.includes(token.id);
		const movable = canMove(token, this.viewer, scene);
		if (this.manageValue && (shiftKey || this.state.local.multi.peek())) {
			return this.setSelection(selected ? selection.filter((id) => id !== token.id) : [...selection, token.id]);
		}
		if (selected && this.manageValue) return this.openMenu(token.id);
		if (selected) return this.select(null);
		if (movable || hasMenu(token, this.viewer, scene)) this.select(token.id);
		if (!movable && isOwn(token, this.viewer)) {
			this.toast(token.locked ? this.labelsValue.refused.locked : this.labelsValue.refused.frozen);
		}
	}

	// Only the master's own board has the menu, and a tap reaches here only
	// with nothing armed and no token under it.
	openEdgeMenu(point: Point) {
		const scene = this.state.view.peek().scene;
		if (!this.manageValue || this.tableValue || !this.edgeMenu || !scene) return false;
		const mark = menuMarkAt(this.gridMath(), scene, edgesOf(scene.edges), point, sceneTerrain(scene.terrain).cuts);
		if (!mark) return false;
		this.select(null);
		this.edgeMenu.open(mark);
		return true;
	}

	// On a piece of a group the menu is the group's, anchored at the last piece selected; on any other it selects the piece alone.
	openMenu(id) {
		const view = this.state.view.peek();
		const token = view.tokens?.[id];
		const selection = this.state.local.selection.peek();
		const group = selection.length > 1 && selection.includes(id);
		const at = group ? selection.at(-1) : id;
		if (!token || !hasMenu(token, this.viewer, view.scene) || this.tokenMenu?.tokenId === at) return;
		if (!group) this.select(id);
		this.tokenMenu?.open(at);
	}

	select(id) {
		this.setSelection(id ? [id] : []);
	}

	// The selection, in the order it was made: the last is the one the menu and its "⋯" belong to. Several pieces mean the group's menu,
	// which follows the last one; with one or none, a menu open on another piece closes.
	setSelection(ids: string[]) {
		const selection = this.state.local.selection.peek();
		if (ids.length !== 1 || selection.length !== 1 || selection[0] !== ids[0]) this.flushStep();
		if (this.state.local.handles.peek() !== (ids.length === 1 ? ids[0] : null)) this.hideHandles();
		this.state.local.selection.value = ids;
		if (!ids.length) this.state.local.multi.value = false;
		else this.selectNote(null);
		const last = ids.at(-1) ?? null;
		const menu = this.tokenMenu;
		if (menu?.tokenId && menu.tokenId !== last) {
			if (last && ids.length > 1) menu.open(last);
			else menu.close();
		}
		this.edgeMenu?.close();
	}

	// *Selecionar várias*: each tap adds or removes a piece, for a touch screen with no Shift. The menu steps aside so the map can be tapped.
	toggleMulti(on = !this.state.local.multi.peek()) {
		if (!this.manageValue) return;
		this.state.local.multi.value = on;
		if (!on) return;
		this.tokenMenu?.close();
		this.toast(this.labelsValue.multi, "notice");
	}

	// The master's resize and rotate handles, on the selected Objeto or
	// Adesivo, or the selected line or area; they go down when it is let go.
	toggleHandles() {
		if (!this.manageValue || this.tableValue) return false;
		const { tokens, drawings } = this.state.view.peek();
		const selection = this.state.local.selection.peek();
		const tokenId = selection.length === 1 ? selection[0] : null;
		const drawingId = this.state.local.drafts.peek().move?.id;
		const token = tokenId && tokens?.[tokenId];
		const drawing = drawingId && drawings?.[drawingId];
		const id = token?.kind === "prop" ? token.id : drawing && drawing.kind !== "text" ? drawing.id : null;
		if (!id) return false;
		if (this.state.local.handles.peek() === id) this.hideHandles();
		else this.state.local.handles.value = id;
		return true;
	}

	// A drag in progress ends as nothing along with them.
	hideHandles() {
		if (!this.state?.local.handles?.peek()) return false;
		this.renderer?.cancelTransform?.();
		this.dropTransform();
		this.dropReshape();
		this.state.local.handles.value = null;
		return true;
	}

	// A handle drag draws from drafts.transform, or from drafts.reshape on a
	// drawing, and writes once, on release.
	transform({ phase, id, gesture, ...attrs }: TransformChange) {
		if (this.state.view.peek().drawings?.[id] || this.state.local.drafts.peek().reshape?.id === id)
			return this.reshape({ phase, id, gesture, points: attrs.points });
		const draft = this.state.local.drafts.peek().transform;
		if (phase === "move") {
			this.state.local.drafts.value = { ...this.state.local.drafts.peek(), transform: { id, ...attrs } };
			return;
		}
		batch(() => {
			this.dropTransform();
			if (phase === "end" && draft?.id === id) this.transformToken(id, draft);
		});
	}

	dropTransform() {
		const { transform, ...drafts } = this.state?.local.drafts.peek() ?? {};
		if (transform) this.state.local.drafts.value = drafts;
	}

	// A drag starts from the drawing as displayed: a nudge still waiting is
	// written first, and the drag is cancelled, never rebased, if the
	// drawing's geometry changes under it (a broadcast, or an optimistic write
	// rolled back).
	reshape({ phase, id, gesture, points }: Pick<TransformChange, "phase" | "id" | "gesture" | "points">) {
		const draft = this.state.local.drafts.peek().reshape;
		if (phase === "start") return this.beginReshape(id);
		if (phase === "move") {
			this.beginReshape(id);
			this.state.local.drafts.value = { ...this.state.local.drafts.peek(), reshape: { id, points } };
			return;
		}
		const base = this.reshapeBase;
		batch(() => {
			this.dropReshape();
			if (phase === "end" && draft?.id === id) this.reshapeDrawing(id, draft.points, gesture, base?.points);
		});
	}

	beginReshape(id: string) {
		if (this.reshapeBase?.id === id) return;
		this.flushMove();
		const drawing = this.state.view.peek().drawings?.[id] as Drawing | undefined;
		if (!drawing) return;
		const base = { id, points: drawing.points, width: drawing.width };
		this.reshapeBase = base;
		this.disposeReshape = effect(() => {
			const now = this.state.view.value.drawings?.[id] as Drawing | undefined;
			if (now && now.width === base.width && now.points.length === base.points.length && now.points.every((value, index) => value === base.points[index]))
				return;
			this.dropReshape();
			this.renderer?.cancelTransform?.();
		});
	}

	dropReshape() {
		this.reshapeBase = null;
		this.disposeReshape?.();
		this.disposeReshape = null;
		const { reshape, ...drafts } = this.state?.local.drafts.peek() ?? {};
		if (reshape) this.state.local.drafts.value = drafts;
	}

	// Written once, through the scene's margin: shifted back onto the scene, or
	// nothing when it cannot fit or the points are those the drag started from.
	reshapeDrawing(id: string, points: number[], gesture: Gesture, from: number[] = []) {
		const { drawings, scene } = this.state.view.peek();
		const fitted = drawings?.[id] && fitOnScene(points, scene);
		if (!fitted || fitted.every((value, index) => Math.abs(value - from[index]) <= RESHAPE_EPSILON)) return;
		this.updateDrawing(id, { points: fitted }, { gesture });
	}

	// Only what changed: a turn alone, or a size or a span with the centre that
	// keeps the opposite corner or edge still, as it is rather than through the
	// snap rule.
	transformToken(id: string, { size, rotation, span, x, y }: Omit<TransformChange, "phase">) {
		const token = this.state.view.peek().tokens?.[id];
		if (!token) return;
		const changes: Omit<TransformChange, "phase" | "id"> = {};
		if (rotation !== undefined && rotation !== (token.rotation ?? 0)) changes.rotation = rotation;
		if (size !== undefined && size !== (token.size ?? 1)) Object.assign(changes, { size, x, y });
		if (span !== undefined && (span ?? 1) !== (token.span ?? 1)) Object.assign(changes, { span, x, y });
		if (Object.keys(changes).length) this.updateToken(id, changes);
	}

	// One thing per press: a tray item armed, a handle drag, the handles, a
	// menu, the dice roller, a terrain brush, the edge tool, the label editor,
	// an unfinished shape, the pen, the fog tool, the selection, the ruler.
	escape() {
		if (this.renderer?.cancelBox?.()) return true;
		if (this.placing) this.disarm();
		else if (this.state?.local.ticks?.peek()) this.state.local.ticks.value = null;
		else if (this.renderer?.cancelTransform?.()) return true;
		else if (this.hideHandles()) return true;
		else if (this.tokenMenu?.tokenId) this.tokenMenu.close();
		else if (this.state?.local.multi?.peek()) this.toggleMulti(false);
		else if (this.edgeMenu?.edge) this.edgeMenu.close();
		else if (this.closeDrawingMenu({ discard: true })) return true;
		else if (this.dice?.closeRoller()) return true;
		else if (this.brush) this.armBrush(null);
		else if (this.edgeKind) this.armEdge(null);
		else if (this.label) this.closeLabel();
		else if (this.shape) this.dropShape();
		else if (this.state?.local.drafts?.peek().move) this.selectDrawing(null);
		else if (this.drawArmed) this.setDraw(false);
		else if (this.noteArmed) this.setNotes(false);
		else if (this.fogArmed) this.setFog(false);
		else if (this.state.local.note?.peek()) this.selectNote(null);
		else if (this.state.local.selection.peek().length) this.select(null);
		else if (this.renderer?.tool === "ruler") this.setRuler(false);
		else return false;
		return true;
	}

	// Keys the site's keyboard-shortcuts controller passes on; true when handled.
	// A focused menu, such as a presence dot's colours or the map menu, keeps its keys.
	handleKey(event) {
		if (this.tableValue || !this.renderer || document.querySelector("dialog[open]")) return false;
		if (document.activeElement?.closest?.('[role="menu"], #map_scene_menu, [data-map-note-card-target="panel"]')) return false;
		const travel = historyKey(event);
		if (travel) {
			if (!this.history) return false;
			this.travel(travel);
			return true;
		}
		const clip = clipboardKey(event);
		if (clip) return this.manageValue && (clip === "copy" ? this.copySelected() : this.pasteCopied());
		if (selectAllKey(event)) return this.manageValue && this.selectAll();
		const step = keyStep(event.key);
		// Arrows move between radios, such as the tray's marker colors.
		if (step) return !inRadioGroup(document.activeElement) && this.stepSelected(step);
		switch (event.key) {
			case "r":
			case "R":
				if (!event.repeat) this.toggleRuler();
				return true;
			case "l":
			case "L":
			case "x":
			case "X":
				if (!this.manageValue) return false;
				if (!event.repeat) this.penKey(event.key.toLowerCase() === "x");
				return true;
			case "g":
			case "G":
				if (!this.manageValue) return false;
				if (!event.repeat) this.toggleGrid();
				return true;
			case "v":
			case "V":
				return this.manageValue && this.toggleHiddenSelected(event.repeat);
			case "h":
			case "H":
				return this.togglePanel("map-dice", event.repeat);
			case "c":
			case "C":
				return this.togglePanel(this.manageValue ? "map-combat" : "map-sheet", event.repeat);
			case "u":
			case "U":
				return this.manageValue && this.togglePanel("map-sound", event.repeat);
			case "]":
				return this.manageValue && this.layerTokens(this.state.local.selection.peek(), "front");
			case "[":
				return this.manageValue && this.layerTokens(this.state.local.selection.peek(), "back");
			case "Enter":
				return this.finishShape();
			case "p":
			case "P":
				return this.toggleTray("tokens", event.repeat);
			case "o":
			case "O":
				return this.toggleTray("objects", event.repeat);
			case "t":
			case "T":
				return this.toggleTray("board", event.repeat);
			case "n":
			case "N":
				return this.toggleTray("fog", event.repeat);
			case "m":
			case "M":
				return this.toggleTray("notes", event.repeat);
			case "j":
			case "J":
				return event.repeat ? Boolean(this.dice?.roller) : Boolean(this.dice?.toggleRoller());
			case "q":
			case "Q":
				return this.turnSelected(-1);
			case "e":
			case "E":
				return this.turnSelected(1);
			case "Delete":
			case "Backspace":
				return this.deleteSelected();
			case "+":
			case "=":
			case "PageUp":
				this.zoomIn();
				return true;
			case "-":
			case "_":
			case "PageDown":
				this.zoomOut();
				return true;
			case "Home":
			case "0":
				this.fit();
				return true;
			case "Escape":
				return this.escape();
			default:
				return false;
		}
	}

	// A selection box let go: the Peças whose centre lies in it join the selection, or the Objetos when it holds no Peça.
	selectBox({ x, y, width, height }: { x: number; y: number; width: number; height: number }) {
		if (!this.manageValue || this.placing || this.state.local.ticks?.peek()) return;
		const inside = Object.values(this.state.view.peek().tokens ?? {})
			.filter((token: { x: number; y: number }) => token.x >= x && token.x <= x + width && token.y >= y && token.y <= y + height)
			.sort(byBandThenZ);
		const caught = inside.some((token) => tokenBand(token) === 1) ? inside.filter((token) => tokenBand(token) === 1) : inside;
		const selection = this.state.local.selection.peek();
		const added = caught.map(({ id }) => id).filter((id) => !selection.includes(id));
		if (added.length) this.setSelection([...selection, ...added]);
	}

	// Ctrl/Cmd+A: every Peça, hidden and fogged ones too, never an Objeto. False leaves the key to the browser.
	selectAll() {
		const pieces = Object.values(this.state.view.peek().tokens ?? {})
			.filter((token: { kind?: string }) => tokenBand(token) === 1)
			.sort(byBandThenZ);
		if (!pieces.length) return false;
		this.selectDrawing(null);
		this.setSelection(pieces.map(({ id }) => id));
		return true;
	}

	// V: hides the selected pieces from the players, or shows them when none is visible.
	toggleHiddenSelected(repeat = false) {
		const tokens = this.state.view.peek().tokens ?? {};
		const chosen = this.state.local.selection.peek().filter((id) => tokens[id]);
		if (!chosen.length) return false;
		if (repeat) return true;
		const hidden = chosen.some((id) => !tokens[id].hidden);
		this.updateTokens(chosen, { hidden });
		const words = this.labelsValue.toggled;
		const key = `${hidden ? "hidden" : "shown"}_${chosen.length > 1 ? "many" : "one"}`;
		this.toast(
			words[key].replace("%{count}", String(chosen.length)).replace("%{name}", () => this.titleOf(tokens[chosen[0]])),
			"notice",
		);
		return true;
	}

	// Front or back of their own band, the group keeping its order, as one step of the history.
	layerTokens(ids: string[], direction: "front" | "back") {
		const writes = layerWrites(this.state.view.peek().tokens ?? {}, ids, direction);
		this.flushStep();
		this.group(() => {
			for (const [id, z] of Object.entries(writes)) this.updateToken(id, { z }, {}, { gesture: direction });
		});
		return ids.length > 0;
	}

	titleOf(token: { name?: string; prefab?: string }) {
		return token.name || this.unlabeledTitlesValue[token.prefab as string] || this.labelsValue.history.piece;
	}

	// A held key repeats; only its first press toggles. Answers whether the
	// tray exists, so a player's P, O or T stays unhandled.
	toggleTray(panel: string, repeat = false) {
		const tray = this.application.getControllerForElementAndIdentifier(this.element, "map-tray") as
			| (Controller & { toggle(event: { params: { panel: string } }): void })
			| null;
		if (!repeat) tray?.toggle({ params: { panel } });
		return Boolean(tray);
	}

	// A panel's key, for a panel whose button is on this page; any other leaves the key alone. Opening takes focus into the panel, where
	// the site's shortcuts ignore keys (the Combate panel's), so focus stays where it was and the same key closes it again.
	togglePanel(identifier: string, repeat = false) {
		const panel = this.application.getControllerForElementAndIdentifier(this.element, identifier) as
			| (Controller & { hasToggleTarget: boolean; toggle(): void })
			| null;
		if (!panel?.hasToggleTarget) return false;
		if (repeat) return true;
		const before = document.activeElement as HTMLElement | null;
		panel.toggle();
		if (document.activeElement !== before) {
			before?.focus?.({ preventScroll: true });
			if (document.activeElement !== before) (document.activeElement as HTMLElement | null)?.blur?.();
		}
		return true;
	}

	// The selected token walks one cell per key, drawn at once from a draft;
	// the write goes once the keys pause, under the same rules as a drag. A
	// selected drawing moves a tenth of a cell instead; a group steps together.
	stepSelected(step) {
		if (this.state.local.drafts.peek().move) return this.nudgeDrawing(step);
		const selection = this.state.local.selection.peek();
		if (selection.length > 1) return this.stepSelectedGroup(selection, step);
		const [id] = selection;
		const view = this.state.view.peek();
		const token = id && view.tokens?.[id];
		if (!token || !isKeyMovable(token) || !canMove(token, this.viewer, view.scene)) return false;
		const from = this.draftOf(token);
		const next = stepToken(this.gridMath(), { ...token, ...from }, step, view.scene);
		if (next) this.draftStep({ ...from, ...next });
		return true;
	}

	// Every selected piece a key moves steps one cell, from where the draft last drew it; an object stays, and when any step would
	// leave the scene the whole step is refused. The keys pause, and one write per piece goes, as one step of the history.
	stepSelectedGroup(ids: string[], step) {
		const view = this.state.view.peek();
		const movable = ids.filter((id) => view.tokens?.[id] && canMove(view.tokens[id], this.viewer, view.scene));
		const group = this.state.local.drafts.peek().group ?? {};
		const from = Object.fromEntries(movable.map((id) => [id, { x: group[id]?.x ?? view.tokens[id].x, y: group[id]?.y ?? view.tokens[id].y }]));
		const moves = stepGroup(this.gridMath(), view.tokens, from, step, view.scene);
		if (!moves) return true;
		if (!moves.length) return false;
		this.draftGroup({ ...group, ...Object.fromEntries(moves.map(({ id, x, y }) => [id, { x, y }])) });
		return true;
	}

	// Q and E turn the selected token; an object ignores the keyboard entirely
	// (`isKeyMovable`) and turns by its handle or the menu's *Ângulo*. A group does not turn.
	turnSelected(dir) {
		const selection = this.state.local.selection.peek();
		if (selection.length > 1) return true;
		const [id] = selection;
		const view = this.state.view.peek();
		const token = id && view.tokens?.[id];
		if (token && !isKeyMovable(token)) return false;
		return this.turnToken(id, dir);
	}

	// One step clockwise (1) or counterclockwise (-1), batched with the key steps.
	turnToken(id, dir) {
		const view = this.state.view.peek();
		const token = id && view.tokens?.[id];
		if (!token || !canTurn(token, this.viewer, view.scene)) return false;
		const from = this.draftOf(token);
		this.draftStep({ ...from, rotation: turn(from.rotation ?? token.rotation, dir, this.gridMath().grid) });
		return true;
	}

	// The menu's *Ângulo*, written at once through the key steps' path, so a
	// player's turn meets the same rules as Q and E.
	rotateToken(id, rotation) {
		const view = this.state.view.peek();
		const token = id && view.tokens?.[id];
		if (!token || !canTurn(token, this.viewer, view.scene)) return false;
		this.draftStep({ ...this.draftOf(token), rotation });
		this.flushStep();
		return true;
	}

	// Same authority as the token menu's "Remover do mapa": the master only.
	deleteSelected() {
		if (!this.manageValue) return false;
		const moving = this.state.local.drafts.peek().move;
		if (moving) {
			batch(() => {
				this.dropMove();
				this.removeDrawing(moving.id);
			});
			return true;
		}
		const noteId = this.state.local.note.peek();
		if (noteId) {
			this.removeNote(noteId);
			return true;
		}
		const selection = this.state.local.selection.peek();
		if (!selection.length) return false;
		this.removeTokens(selection);
		return true;
	}

	// Only what the keys changed, so a turn never writes back a position another
	// client moved the token to meanwhile.
	draftOf(token): StepDraft {
		const { step } = this.state.local.drafts.peek();
		return step?.id === token.id ? step : { id: token.id };
	}

	draftGroup(group: Record<string, { x: number; y: number }>) {
		if (this.state.local.ticks?.peek()) return;
		this.state.local.drafts.value = { ...this.state.local.drafts.peek(), group };
		this.keepSelectionVisible();
		clearTimeout(this.stepTimer);
		this.stepTimer = setTimeout(() => this.flushStep(), STEP_WRITE_MS);
	}

	draftStep(step: StepDraft) {
		if (this.state.local.ticks?.peek()) return;
		this.state.local.drafts.value = { ...this.state.local.drafts.peek(), step };
		this.keepSelectionVisible();
		clearTimeout(this.stepTimer);
		this.stepTimer = setTimeout(() => this.flushStep(), STEP_WRITE_MS);
	}

	// A step whose token is gone, or hidden from this viewer, is dropped.
	flushStep() {
		this.flushGroup();
		const step = this.state?.local.drafts.peek().step;
		if (!step) return;
		const token = this.state.view.peek().tokens?.[step.id];
		if (token && isVisible(token, this.viewer)) this.moveToken(step.id, { x: step.x ?? token.x, y: step.y ?? token.y });
		else this.dropStep(step.id);
	}

	// The group the keys stepped, written as one move.
	flushGroup() {
		const { group, ...drafts } = this.state?.local.drafts.peek() ?? {};
		if (!group) return;
		clearTimeout(this.stepTimer);
		batch(() => {
			this.state.local.drafts.value = drafts;
			this.moveTokens(Object.entries(group).map(([id, at]) => ({ id, ...at })));
		});
	}

	dropStep(id) {
		const { step, ...drafts } = this.state.local.drafts.peek();
		if (step?.id !== id) return;
		clearTimeout(this.stepTimer);
		this.state.local.drafts.value = drafts;
	}

	// A key step of the group still waiting for this piece is superseded, and with the last of them goes the timer.
	dropGroupStep(id) {
		const { group, ...drafts } = this.state.local.drafts.peek();
		if (!group?.[id]) return;
		const { [id]: _gone, ...rest } = group;
		const left = Object.keys(rest).length > 0;
		if (!left) clearTimeout(this.stepTimer);
		this.state.local.drafts.value = left ? { ...drafts, group: rest } : drafts;
	}

	// The server stamps the colour everyone else sees.
	ping(point) {
		const scene = this.state.view.peek().scene;
		if (!scene) return;
		const color = this.participant(this.accountIdValue)?.color;
		this.renderer?.showPing({ x: point.x, y: point.y, color, accountId: this.accountIdValue });
		this.subscription?.perform("ping", { scene_id: scene.id, x: point.x, y: point.y });
	}

	// Rounded like a drawing's points, to keep the payload short. The master's
	// ruler with an end in the fog goes out as a release, so it shows on no
	// other board until it leaves the fog.
	sendRuler(points: RulerPoints | null, kind?: RulerKind) {
		const scene = this.state.view.peek().scene;
		if (!scene) return;
		const fogged = points && this.manageValue && endsInFog(points, this.gridMath(), scene, scene.fog);
		if (fogged) this.noticeRulerInFog();
		const shared = fogged ? null : points;
		const rounded = shared?.map((value) => Math.round(value * 10) / 10);
		const shape = kind && kind.shape !== "ruler" ? { shape: kind.shape, ...(kind.width ? { width: kind.width } : {}) } : {};
		this.subscription?.perform("ruler", rounded ? { scene_id: scene.id, points: rounded, ...shape } : { scene_id: scene.id });
	}

	// Once per browser, or once per board where storage is blocked.
	noticeRulerInFog() {
		if (this.rulerFogNoticed || !this.rulerHelpUrlValue) return;
		this.rulerFogNoticed = true;
		try {
			if (localStorage.getItem(RULER_FOG_NOTICED)) return;
			localStorage.setItem(RULER_FOG_NOTICED, "1");
		} catch {}
		this.toast(this.labelsValue.rulerInFog, "notice", this.rulerHelpUrlValue);
	}

	participant(accountId) {
		return this.state.view.peek().participants?.[accountId];
	}

	gridMath() {
		const scene = this.state.view.peek().scene;
		return gridMath(this.state.local.drafts.peek().grid ?? scene.grid);
	}

	arm(getSubject) {
		this.armBrush(null);
		this.armEdge(null);
		this.setDraw(false);
		this.setFog(false);
		this.setNotes(false);
		this.placing = getSubject;
		this.select(null);
	}

	// A terrain code, or "." to erase, paints what the next strokes cross; null
	// puts the brush down. While it is up a tap never selects a token.
	armBrush(code) {
		if (!code && !this.brush) return;
		if (code) {
			this.disarm();
			this.select(null);
			this.setRuler(false);
			this.setDraw(false);
			this.setFog(false);
			this.setNotes(false);
			this.armEdge(null);
		}
		this.brush = code || null;
		this.renderer?.setTool(this.brush ? "paint" : "move");
		this.dispatch("brush", { detail: { code: this.brush } });
	}

	// The tray's choice of what the swatch paints with: the brush, a rectangle
	// dragged out, or a fill from the cell tapped.
	setPaintTool(tool: "brush" | "rectangle" | "fill") {
		this.paintTool = tool;
		this.paint({ phase: "cancel", cells: [] });
	}

	setPaintBorder(letter: string) {
		this.paintBorder = letter;
		this.paint({ phase: "cancel", cells: [] });
	}

	// A stroke keeps the cells it crossed, a rectangle the cells between its
	// corners, a fill the region under the tap when it ends. All draw at once
	// over the scene's current terrain, so a change another tab makes meanwhile
	// shows and survives; one write goes out when the gesture ends, and none when
	// it changed nothing.
	paint({ phase, cells, from, to }: { phase: string; cells: StrokeCell[]; from?: Point; to?: Point }) {
		const scene = this.state.view.peek().scene;
		// The edges draft is a stroke's own wall on a diagonal; the edge tool has the board's other one.
		const { terrain: draft, edges: _wall, ...drafts } = this.state.local.drafts.peek();
		if (phase === "start" || phase === "cancel") this.rectKey = null;
		if (phase === "cancel" || !this.brush || !scene) {
			this.stroke = this.diagonal = null;
			if (draft) this.state.local.drafts.value = drafts;
			return;
		}
		if (this.brush === DIAGONAL) return this.paintDiagonal(phase, cells, scene, drafts);
		const math = this.gridMath();
		const { rows: kept, cuts, corners, borders } = sceneTerrain(scene.terrain);
		const tool = this.paintTool;
		const border = this.paintBorder;
		let rows = kept;
		let painting: Cell[] = [];
		let touched: Touch[] | null = null;
		if (tool === "fill") {
			if (phase !== "end" || !from) return;
			// A tap: a release on another cell than the press fills nothing.
			const press = math.cellAt(from.x, from.y);
			const release = to ? math.cellAt(to.x, to.y) : press;
			if (press.col === release.col && press.row === release.row) {
				painting = fillCells(math, scene, kept, press);
				rows = paintCells(kept, painting, this.brush);
			}
		} else if (tool === "rectangle") {
			if (!from || !to) return;
			const [a, b] = [math.cellAt(from.x, from.y), math.cellAt(to.x, to.y)];
			const key = `${a.col},${a.row},${b.col},${b.row}`;
			if (phase === "move" && key === this.rectKey) return;
			this.rectKey = key;
			painting = rectCells(math, scene, from, to);
			rows = paintCells(kept, painting, this.brush);
		} else {
			if (phase === "start" || !this.stroke) this.stroke = new Map();
			for (const { x, y, points, ...cell } of cells) {
				if (!paintable(math, scene, cell)) continue;
				const key = `${cell.col},${cell.row}`;
				const touch = this.stroke.get(key) ?? { ...cell, points: [] };
				// A cell with no point (a test's, or the stroke's) is whole to the brush.
				for (const point of points ?? (x === undefined || y === undefined ? [] : [{ x, y }])) touch.points.push(fractionIn(math, cell, point));
				this.stroke.set(key, touch);
			}
			touched = [...this.stroke.values()];
		}
		// A brush on a cut cell paints the half under it, and may write the wall between two floors with it. Otherwise the main half
		// is painted: rock there takes the cut with it, or flips a cut whose corner is floor, and any other code leaves the corner as it is.
		const edges = edgesOf(scene.edges);
		const painted = touched
			? paintTouches({ rows: kept, cuts, corners, borders }, edges, touched, this.brush, border)
			: { terrain: terrainOf(rows, cuts, corners, this.brush === EMPTY ? borders : setBorders(borders, painting, border)), edges };
		const { terrain } = painted;
		if (phase !== "end") {
			this.state.local.drafts.value = { ...drafts, terrain, ...(painted.edges !== edges ? { edges: painted.edges } : {}) };
			return;
		}
		this.stroke = this.diagonal = null;
		batch(() => {
			this.state.local.drafts.value = drafts;
			// A gesture that painted nothing writes nothing,
			if (touched?.length || painting.length) this.writeGeometry(scene, terrain, painted.edges);
		});
	}

	// One request for the terrain and the edges, as the joint step leaves them (maps/geometry.ts).
	writeGeometry(scene, terrain: Terrain, edges: Edges) {
		const write = geometryWrite(scene, terrain, edges);
		if (!write) return;
		if (write.attrs.edges) this.updateScene(write.attrs, write.local);
		else this.updateScene(write.attrs);
	}

	// Diagonal. A swipe turns a cell once the pointer has been inside its middle
	// 60%, so brushing past a corner or along an edge touches nothing, and only
	// where the neighbours settle it; each is judged against the terrain as the
	// stroke has left it so far, so swiping along steps turns each in turn. A tap,
	// a stroke that ends in the cell it started in, also takes the corner
	// nearest the press where the neighbours do not settle it, decided on release.
	// The stroke keeps its working terrain and lays only the cells an event turns
	// on it, one normalisation per event, and publishes a draft only then. The
	// cells turned are kept as well: when the scene's terrain changes under the
	// stroke (another tab), they are laid again over the new one.
	paintDiagonal(phase: string, cells: StrokeCell[], scene, drafts: Record<string, unknown>) {
		if (scene.grid?.kind !== "square") return;
		const math = this.gridMath();
		if (phase === "start" || !this.diagonal) this.diagonal = { turned: new Map(), origin: null, work: null, basis: undefined };
		const stroke = this.diagonal;
		let changed = false;
		if (!stroke.work || stroke.basis !== scene.terrain) {
			const base = sceneTerrain(scene.terrain);
			stroke.work = applyCells(base, [...stroke.turned.values()]);
			stroke.basis = scene.terrain;
			changed = stroke.turned.size > 0;
		}
		const open = (col: number, row: number) => paintable(math, scene, { col, row });
		const pointerIn = (cell: Cell, { x, y }: { x?: number; y?: number }) =>
			x === undefined || y === undefined ? { x: 0.5, y: 0.5 } : fractionIn(math, cell, { x, y });
		const [rows, cuts, corners, borders] = [[...stroke.work.rows], [...stroke.work.cuts], [...(stroke.work.corners ?? [])], [...(stroke.work.borders ?? [])]];
		const turn = (key: string, cell: Cell, change: CellChange | null) => {
			if (!change) return;
			stroke.turned.set(key, { cell, change });
			layCell(rows, cell.col, cell.row, change.code);
			layCell(cuts, cell.col, cell.row, change.cut ?? EMPTY);
			layCell(corners, cell.col, cell.row, change.corner ?? EMPTY);
			if (change.border) layCell(borders, cell.col, cell.row, change.border);
			changed = true;
		};
		for (const { x, y, points, ...cell } of cells) {
			const key = `${cell.col},${cell.row}`;
			const samples = points ?? [{ x, y }];
			if (!stroke.origin) stroke.origin = { key, cell, pointer: pointerIn(cell, samples[0]), left: false };
			else if (key !== stroke.origin.key) stroke.origin.left = true;
			if (stroke.turned.has(key)) continue;
			const inside = samples.map((point) => pointerIn(cell, point)).find(INNER);
			if (inside) turn(key, cell, diagonalChange({ rows, cuts, corners, borders }, cell, inside, { open }));
		}
		const { origin } = stroke;
		if (phase === "end" && origin && !origin.left && !stroke.turned.has(origin.key)) {
			turn(origin.key, origin.cell, diagonalChange({ rows, cuts, corners, borders }, origin.cell, origin.pointer, { open, fallback: true }));
		}
		if (changed) stroke.work = terrainOf(rows, cuts, corners, borders);
		if (phase !== "end") {
			if (changed) this.state.local.drafts.value = { ...drafts, terrain: stroke.work };
			return;
		}
		this.stroke = this.diagonal = null;
		const written = stroke.work;
		batch(() => {
			this.state.local.drafts.value = drafts;
			if (stroke.turned.size) this.writeGeometry(scene, written, edgesOf(scene.edges));
		});
	}

	// The plain code of a brush ("p" Porta, "j" Janela, "e" Seteira, "g" Grade,
	// "m" Parede) or "." for their Apagar, on a square scene's grid lines; null puts the tool down.
	// It puts the brush, the pen, the fog tool, the ruler and an armed tray item
	// down, and each of them puts it down. While it is up a tap never selects
	// or pings.
	armEdge(kind: string | null) {
		if (!kind && !this.edgeKind) return;
		if (kind) {
			if (!this.manageValue || this.tableValue || this.state.view.peek().scene?.grid?.kind !== "square") return;
			this.disarm();
			this.armBrush(null);
			this.setDraw(false);
			this.setFog(false);
			this.setNotes(false);
			this.setRuler(false);
			this.select(null);
		}
		this.dropEdgeGesture();
		this.edgeKind = kind || null;
		this.renderer?.setTool(this.edgeKind ? "edge" : "move");
		this.dispatch("edge", { detail: { kind: this.edgeKind } });
	}

	// A tap takes the side nearest it, a drag every grid-line segment it runs
	// along, skipping the unpaintable ones. The result draws at once from
	// drafts.edges, laid over the scene's current edges so a change another tab
	// makes meanwhile survives, with the edges taken in drafts.edgePick; one
	// write goes out when the gesture ends, none when it changed nothing.
	edge({ phase, from, to }: EdgeGesture) {
		const scene = this.state.view.peek().scene;
		const kind = this.edgeKind;
		const previous = phase === "start" ? null : this.edgeStroke;
		if (phase === "cancel" || !kind || !scene || !from || !to || (phase !== "start" && !previous)) return this.dropEdgeGesture();
		const math = this.gridMath();
		const tap = from.x === to.x && from.y === to.y;
		const stroke = !previous || (previous.tap && !tap) ? { taken: new Map<string, Mark>(), last: from, tap } : previous;
		const fresh = stroke !== previous;
		this.edgeStroke = stroke;
		const before = stroke.taken.size;
		const { cuts } = sceneTerrain(scene.terrain);
		for (const edge of tap ? [nearestSide(math, from, cuts)] : dragEdges(math, stroke.last, to, cuts)) {
			if (edge && edgePaintable(math, scene, edge)) stroke.taken.set(edgeId(edge), edge);
		}
		if (!tap) stroke.last = to;
		const drafts = this.state.local.drafts.peek();
		if (phase === "move" && !fresh && stroke.taken.size === before && stroke.basis === scene.edges) return;
		stroke.basis = scene.edges;
		const current = edgesOf(scene.edges);
		const taken = [...stroke.taken.values()];
		if (!stroke.mode && taken.length) stroke.mode = gestureMode(current, taken[0], kind);
		const edges = stroke.mode ? applyEdgeGesture(current, taken, kind, stroke.mode) : current;
		const { edges: _drafted, edgePick: _picked, ...rest } = drafts;
		if (phase !== "end") {
			this.state.local.drafts.value = { ...rest, edges, edgePick: taken };
			return;
		}
		this.edgeStroke = null;
		batch(() => {
			this.state.local.drafts.value = rest;
			this.writeGeometry(scene, sceneTerrain(scene.terrain), edges);
		});
	}

	dropEdgeGesture() {
		this.edgeStroke = null;
		const { edges, edgePick, ...drafts } = this.state?.local.drafts.peek() ?? {};
		if (edges || edgePick) this.state.local.drafts.value = drafts;
	}

	get drawChoices(): DrawChoices {
		const preset = defaultPreset(this.state?.view.peek().scene?.grid?.kind);
		this.drawChoice ??= {
			mode: "free",
			preset,
			color: "azul",
			size: "medium",
			style: "solid",
			label: "medium",
			eraser: false,
			move: false,
			snap: PRESETS[preset].snap,
		};
		return this.drawChoice;
	}

	// L picks the pen up in the last line mode and X in Texto, both without the
	// eraser; the key of the mode already up puts it down.
	penKey(text: boolean) {
		if (this.drawArmed && (this.drawChoices.mode === "text") === text) return this.setDraw(false);
		this.armDraw({ mode: text ? "text" : (this.lineMode ?? "free"), eraser: false, move: false });
	}

	// The master's pen, with the tray's Desenho choices, which last the
	// session. It puts the ruler, the brush and an armed tray item down, and
	// each of them puts it down. The eraser and *Mover desenho* exclude each other.
	// Another style sets snapping to its own; flipping snapping alone keeps the
	// shape being drawn.
	armDraw(choices: Partial<DrawChoices> = {}) {
		if (!this.manageValue || this.tableValue) return;
		const restyled = choices.preset !== undefined && choices.preset !== this.drawChoices.preset;
		if (restyled && choices.preset === "livre") choices = { ...this.livreStart(), ...choices };
		this.drawChoice = {
			...this.drawChoices,
			...(restyled ? { snap: PRESETS[choices.preset]?.snap ?? false } : {}),
			...choices,
			...(choices.eraser ? { move: false } : choices.move ? { eraser: false } : {}),
		};
		// The server refuses a ledge or a tunnel on an area, so Área takes the set's first style instead.
		if (this.drawChoice.mode === "area" && isStyled(PRESETS[this.drawChoice.preset]?.style ?? "solid")) {
			const preset = defaultPreset(this.state?.view.peek().scene?.grid?.kind);
			this.drawChoice = { ...this.drawChoice, preset, snap: PRESETS[preset].snap };
		}
		if (this.drawChoice.mode !== "text") this.lineMode = this.drawChoice.mode;
		if (!this.drawChoice.move) this.selectDrawing(null);
		if (Object.keys(choices).join() !== "snap") this.dropShape();
		this.keepLabel();
		if (!this.drawArmed) {
			this.disarm();
			this.armBrush(null);
			this.armEdge(null);
			this.setFog(false);
			this.setNotes(false);
			this.setRuler(false);
			this.select(null);
			this.drawArmed = true;
			this.renderer?.setTool("draw");
		}
		this.showDraw();
	}

	// Livre starts from the colour, width and stroke of the style the pen was on.
	livreStart() {
		const from = PRESETS[this.drawChoices.preset];
		return from ? { color: from.color, size: from.size, style: isStyled(from.style) ? "solid" : from.style } : {};
	}

	setDraw(on: boolean) {
		if (on) return this.armDraw();
		if (!this.drawArmed) return;
		this.closeLabel();
		this.dropShape();
		this.selectDrawing(null);
		this.drawArmed = false;
		this.renderer?.setTool("move");
		this.showDraw();
	}

	showDraw() {
		this.dispatch("draw", { detail: { on: Boolean(this.drawArmed), ...this.drawChoices } });
	}

	get fogChoices(): FogChoices {
		this.fogChoice ??= { mode: "reveal", shape: "rectangle" };
		return this.fogChoice;
	}

	// The master's fog tool, with the Névoa panel's mode and shape, only while
	// the scene's fog is on. It puts the ruler, the brush, the pen and an armed
	// tray item down, and each of them puts it down.
	armFog(choices: Partial<FogChoices> = {}) {
		if (!this.manageValue || this.tableValue) return;
		this.fogChoice = { ...this.fogChoices, ...choices };
		if (!this.state.view.peek().scene?.fog?.enabled) return this.showFog();
		if (!this.fogArmed) {
			this.disarm();
			this.armBrush(null);
			this.armEdge(null);
			this.setDraw(false);
			this.setNotes(false);
			this.setRuler(false);
			this.select(null);
			this.fogArmed = true;
			this.renderer?.setTool("fog");
		}
		this.showFog();
	}

	setFog(on: boolean, enabled?: boolean) {
		if (on) return this.armFog();
		if (!this.fogArmed) return;
		this.dropFogStroke();
		this.fogArmed = false;
		this.renderer?.setTool("move");
		this.showFog(enabled);
	}

	showFog(enabled = Boolean(this.state.view.peek().scene?.fog?.enabled)) {
		this.dispatch("fog", { detail: { on: Boolean(this.fogArmed), enabled, ...this.fogChoices } });
	}

	// A brush stroke keeps every paintable cell it crosses; a rectangle the
	// cells whose centre lies inside it, a tap the one under it. Both draw at
	// once from the draft over the scene's current fog, with the cells taken
	// highlighted (drafts.fogPick, the same list the rows are painted from),
	// and one write goes out when the gesture ends, painted over the fog as it
	// is then, none when it changed nothing. A move that takes no new cell over
	// the same fog and mode keeps the drafts as they were.
	fogGesture({ phase, from, to, cells }: FogGesture) {
		const scene = this.state.view.peek().scene;
		if (phase === "cancel" || !this.fogArmed || !scene?.fog?.enabled) return this.dropFogStroke();
		const math = this.gridMath();
		const { mode, shape } = this.fogChoices;
		const state: FogState = mode === "reveal" ? "revealed" : "hidden";
		const { fog: draft, fogPick, ...drafts } = this.state.local.drafts.peek();
		const kept = phase === "start" ? null : fogPick;
		const rect = shape === "brush" ? null : rectOf(from as Point, to as Point);
		let taken: Cell[];
		if (rect) {
			const inRect = cellsInRect(math, scene, rect);
			taken = kept && sameCells(kept.cells, inRect) ? kept.cells : inRect;
		} else {
			if (phase === "start" || !this.fogStroke) this.fogStroke = new Map();
			const stroke = this.fogStroke;
			const before = stroke.size;
			for (const cell of cells) {
				const key = `${cell.col},${cell.row}`;
				if (!stroke.has(key) && paintable(math, scene, cell)) stroke.set(key, cell);
			}
			taken = kept && stroke.size === before ? kept.cells : [...stroke.values()];
		}
		const current = normalizeRows(fogRows(scene.fog));
		const paint = () => normalizeRows(paintFog(current, taken, state, scene.fog.default));
		if (phase === "end") {
			const rows = paint();
			this.fogStroke = null;
			batch(() => {
				this.state.local.drafts.value = drafts;
				if (rows.join("\n") !== current.join("\n")) this.updateScene({ fog: { rows } }, { fog: { ...scene.fog, rows } });
			});
			return;
		}
		const unchanged = draft && kept && taken === kept.cells && kept.basis === scene.fog && kept.state === state;
		if (unchanged && !rect) return;
		const fog = unchanged ? draft : { ...scene.fog, rows: paint() };
		this.state.local.drafts.value = { ...drafts, fog, fogPick: { state, cells: taken, rect, basis: scene.fog } };
	}

	dropFogStroke() {
		this.fogStroke = null;
		const { fog, fogPick, ...drafts } = this.state?.local.drafts.peek() ?? {};
		if (fog || fogPick) this.state.local.drafts.value = drafts;
	}

	// Shows or hides the grid for everyone, as the panel's switch does, as a step of the history.
	toggleGrid() {
		const { grid } = this.state.view.peek().scene;
		const visible = grid.visible === false;
		this.updateScene({ grid: { visible } }, { grid: { ...grid, visible } });
	}

	// Turning the fog on starts from the scene's stored default, hidden when it
	// has none; turning it off keeps the painting for later.
	setFogEnabled(on: boolean) {
		const fog = this.state.view.peek().scene?.fog ?? {};
		this.updateScene({ fog: { enabled: on } }, { fog: { ...fog, enabled: on } });
		if (!on) this.setFog(false, false);
	}

	fogAll(state: FogState) {
		const fog = this.state.view.peek().scene?.fog ?? {};
		this.updateScene({ fog: { default: state, rows: [] } }, { fog: { ...fog, default: state, rows: [] } });
	}

	drawGesture({ phase, point, tap }: DrawGesture) {
		if (!this.drawArmed) return;
		const { mode, eraser, move } = this.drawChoices;
		if (phase === "menu") {
			if (move) this.menuAt(point);
		} else if (move) {
			this.moveGesture(phase, point, tap);
		} else if (eraser) {
			if (phase === "end" && tap) this.eraseAt(point);
		} else if (mode === "text") {
			if (phase === "end" && tap) this.tapLabel(point);
		} else if (mode === "free") {
			this.freehand(phase, point);
		} else {
			this.addVertex(phase, point);
		}
	}

	freehand(phase: DrawGesture["phase"], point: DrawGesture["point"]) {
		if (phase === "cancel") return this.dropShape();
		if (phase === "hover") return;
		if (phase === "start") this.shape = [];
		if (!this.shape) return;
		if (phase !== "end") {
			this.shape.push(...this.onScene(point));
			return this.showShape();
		}
		const raw = this.shape;
		const cell = this.gridMath().grid;
		let tolerance = SIMPLIFY_CELL * Math.min(cell.cell_w, cell.cell_h);
		let points = simplify(raw, tolerance);
		while (points.length / 2 > MAX_DRAWING_POINTS) {
			tolerance *= 2;
			points = simplify(raw, tolerance);
		}
		batch(() => {
			this.dropShape();
			if (points.length >= 4) this.createDrawing("line", points);
		});
	}

	// Each release adds a vertex; one on the last vertex ends the shape, so a
	// double tap does too. A second finger drops the press, not the shape.
	// Snapped, it ends only on that very point, since far out the next target
	// can lie within reach; a double tap snaps twice onto it.
	addVertex(phase: DrawGesture["phase"], raw: DrawGesture["point"]) {
		if (phase === "cancel") return this.shape && this.showShape();
		const { snap } = this.drawChoices;
		const point = raw && snap ? nearestSnap(this.gridMath(), raw) : raw;
		if (phase !== "end") return this.shape && this.showShape(point);
		const vertices = this.shape ?? [];
		const count = vertices.length / 2;
		const [lastX, lastY] = vertices.slice(-2);
		const [x, y] = this.onScene(point);
		const onLast = snap ? x === lastX && y === lastY : Math.hypot(point.x - lastX, point.y - lastY) <= this.drawReach();
		if (count && onLast) {
			this.finishShape();
			return;
		}
		if (count >= MAX_DRAWING_POINTS) return;
		this.shape = [...vertices, x, y];
		this.showShape();
	}

	// True when there was a shape to finish, even one still too short.
	finishShape() {
		const { mode } = this.drawChoices;
		if (!this.drawArmed || mode === "free" || !this.shape) return false;
		const kind = mode === "area" ? "area" : "line";
		if (this.shape.length / 2 < (kind === "area" ? 3 : 2)) return true;
		const points = this.shape;
		batch(() => {
			this.dropShape();
			this.createDrawing(kind, points);
		});
		return true;
	}

	showShape(pointer: DrawGesture["point"] = null) {
		const drafts = this.state.local.drafts.peek();
		const points = pointer ? [...this.shape, pointer.x, pointer.y] : [...this.shape];
		const kind = this.drawChoices.mode === "area" ? "area" : "line";
		this.state.local.drafts.value = { ...drafts, drawing: { kind, points, ...presetStyle(this.drawChoices, this.gridMath().grid) } };
	}

	dropShape() {
		this.shape = null;
		const { drawing, ...drafts } = this.state?.local.drafts.peek() ?? {};
		if (drawing) this.state.local.drafts.value = drafts;
	}

	eraseAt(point: DrawGesture["point"]) {
		const hit = this.drawingAt(point);
		if (hit) this.removeDrawing(hit.id);
	}

	drawingAt(point: DrawGesture["point"]) {
		const drawings = Object.values(this.state.view.peek().drawings ?? {}) as Drawing[];
		return hitDrawing(drawings, point, this.drawReach(), this.renderer?.labelWidth, this.gridMath().grid);
	}

	// A press on a drawing selects it and a drag moves it, written once on
	// release; a tap on nothing lets go. A tap without a drag on the drawing
	// already selected, or on its badge, opens its menu. Drawings never snap.
	moveGesture(phase: DrawGesture["phase"], point: DrawGesture["point"], tap: boolean) {
		const grab = this.grab;
		if (phase === "start") {
			const selected = this.state.local.drafts.peek().move?.id;
			const badge = Boolean(selected && this.renderer?.drawingBadgeAt?.(point));
			const hit = badge ? null : this.drawingAt(point);
			this.flushMove();
			if (hit) this.selectDrawing(hit.id);
			if (badge) this.grab = { id: selected, from: point, badge };
			else this.grab = hit ? { id: hit.id, from: point, again: hit.id === selected } : null;
		} else if (phase === "move" && grab && !grab.badge) {
			const drawing = this.state.view.peek().drawings?.[grab.id] as Drawing | undefined;
			if (!drawing) return;
			const shift = clampShift(drawing.points, point.x - grab.from.x, point.y - grab.from.y, this.state.view.peek().scene);
			this.state.local.drafts.value = { ...this.state.local.drafts.peek(), move: { id: grab.id, ...shift } };
		} else if (phase === "cancel" && grab) {
			this.grab = null;
			if (!grab.badge) this.state.local.drafts.value = { ...this.state.local.drafts.peek(), move: { id: grab.id, dx: 0, dy: 0 } };
		} else if (phase === "end") {
			this.grab = null;
			if (grab) this.flushMove();
			if (grab && tap && (grab.again || grab.badge)) this.openDrawingMenu(grab.id);
			else if (!grab && tap) this.selectDrawing(null);
		}
	}

	// A right click selects the drawing under it and opens its menu.
	menuAt(point: DrawGesture["point"]) {
		const hit = this.drawingAt(point);
		if (!hit) return;
		this.selectDrawing(hit.id);
		this.openDrawingMenu(hit.id);
	}

	openDrawingMenu(id: string) {
		if (!this.manageValue || this.tableValue || !this.state.view.peek().drawings?.[id]) return;
		this.state.local.drawingMenu.value = id;
		this.drawingMenu?.open(id);
	}

	// Whatever closes it writes a text typed in it, but Escape, which drops it.
	closeDrawingMenu({ discard = false } = {}) {
		if (!this.state?.local.drawingMenu?.peek()) return false;
		this.state.local.drawingMenu.value = null;
		this.drawingMenu?.close({ discard });
		return true;
	}

	// Selecting another drawing, or none, writes what the keys left waiting.
	selectDrawing(id: string | null) {
		const drafts = this.state?.local.drafts.peek();
		if (!drafts || drafts.move?.id === id || (!id && !drafts.move)) return;
		this.flushMove();
		this.dropMove();
		if (!id) return;
		this.state.local.drafts.value = { ...this.state.local.drafts.peek(), move: { id, dx: 0, dy: 0 } };
		// A drawing removed elsewhere (another tab, a resync) leaves the selection.
		this.disposeMove = effect(() => {
			if (!this.state.view.value.drawings?.[id]) this.dropMove();
		});
	}

	// Letting a drawing go takes its menu and its handles with it.
	dropMove() {
		clearTimeout(this.moveTimer);
		this.grab = null;
		this.disposeMove?.();
		this.disposeMove = null;
		const move = this.state.local.drafts.peek().move;
		if (!move) return;
		this.closeDrawingMenu();
		if (this.state.local.handles.peek() === move.id) this.hideHandles();
		const { move: _dropped, ...drafts } = this.state.local.drafts.peek();
		this.state.local.drafts.value = drafts;
	}

	// A drag in progress owns the offset, so the keys wait for it.
	nudgeDrawing([cols, rows]: [number, number]) {
		if (this.grab || this.reshapeBase) return true;
		const { move } = this.state.local.drafts.peek();
		const { scene, drawings } = this.state.view.peek();
		const drawing = drawings?.[move.id] as Drawing | undefined;
		if (!drawing) return false;
		const { cell_w, cell_h } = this.gridMath().grid;
		const step = NUDGE_CELL * Math.min(cell_w, cell_h);
		const shift = clampShift(drawing.points, move.dx + cols * step, move.dy + rows * step, scene);
		this.state.local.drafts.value = { ...this.state.local.drafts.peek(), move: { id: move.id, ...shift } };
		clearTimeout(this.moveTimer);
		this.moveTimer = setTimeout(() => this.flushMove(), STEP_WRITE_MS);
		return true;
	}

	// The points are read, and the offset clamped again, at the write, so a
	// drawing another tab moved meanwhile moves from where it now is.
	flushMove() {
		clearTimeout(this.moveTimer);
		const move = this.state?.local.drafts.peek().move;
		if (!move || (!move.dx && !move.dy)) return;
		const { drawings, scene } = this.state.view.peek();
		const drawing = drawings?.[move.id] as Drawing | undefined;
		const shift = drawing && clampShift(drawing.points, move.dx, move.dy, scene);
		batch(() => {
			this.state.local.drafts.value = { ...this.state.local.drafts.peek(), move: { id: move.id, dx: 0, dy: 0 } };
			if (shift && (shift.dx || shift.dy)) this.updateDrawing(move.id, { points: translate(drawing.points, shift.dx, shift.dy) });
		});
	}

	drawReach() {
		return DRAW_REACH_PX / this.state.local.viewport.peek().zoom;
	}

	// The server refuses points past the scene's margin; the rounding keeps the
	// payload short.
	onScene({ x, y }: { x: number; y: number }) {
		const { width_px: width, height_px: height } = this.state.view.peek().scene;
		const round = (value: number, most: number) => Math.round(Math.min(most, Math.max(0, value)) * 10) / 10;
		return [round(x, width), round(y, height)];
	}

	// A tap while the editor is open saves what was typed; otherwise it opens
	// the menu of the selected label when it hits the badge, selects and opens
	// the menu of the label it hits, or opens the editor for a new one there.
	tapLabel(point: DrawGesture["point"]) {
		if (this.label) return this.saveLabel(this.label.text);
		const selected = this.state.local.drafts.peek().move?.id;
		if (selected && this.renderer?.drawingBadgeAt?.(point)) return this.openDrawingMenu(selected);
		const labels = Object.values(this.state.view.peek().drawings ?? {}).filter((drawing: Drawing) => drawing.kind === "text") as Drawing[];
		const hit = hitDrawing(labels, point, this.drawReach(), this.renderer?.labelWidth);
		this.selectDrawing(hit?.id ?? null);
		if (hit) return this.openDrawingMenu(hit.id);
		const { color, label } = this.drawChoices;
		this.label = { points: this.onScene(point), text: "", color, width: labelSize(label, this.gridMath().grid), style: "solid" };
		this.labelEditor?.open(this.label);
		this.previewLabel(this.label.text);
	}

	previewLabel(text: string) {
		if (!this.label) return;
		this.label = { ...this.label, text };
		const { drawing, ...drafts } = this.state.local.drafts.peek();
		this.state.local.drafts.value = text ? { ...drafts, drawing: { kind: "text", ...this.label } } : drafts;
	}

	// The label follows the tab's colour and size; another mode or the
	// eraser closes the editor.
	keepLabel() {
		if (!this.label) return;
		const { mode, eraser, move, color, label } = this.drawChoices;
		if (mode !== "text" || eraser || move) return this.closeLabel();
		this.label = { ...this.label, color, width: labelSize(label, this.gridMath().grid) };
		this.previewLabel(this.label.text);
	}

	// An empty text drops the label.
	saveLabel(text: string) {
		const label = this.label;
		if (!label) return;
		const trimmed = text.replace(/\s+/g, " ").trim();
		batch(() => {
			this.closeLabel();
			if (trimmed) this.createDrawing("text", label.points, { color: label.color, width: label.width, style: "solid", text: trimmed });
		});
	}

	// The menu's text as it is typed, drawn in place of the stored label.
	previewDrawingText(id: string, text: string) {
		const label = this.state.view.peek().drawings?.[id] as Drawing | undefined;
		if (!label) return;
		const { drawing, ...drafts } = this.state.local.drafts.peek();
		this.state.local.drafts.value = {
			...drafts,
			drawing: { kind: "text", id, points: label.points, color: label.color, width: label.width, style: "solid", text },
		};
	}

	clearDrawingPreview() {
		const { drawing, ...drafts } = this.state?.local.drafts.peek() ?? {};
		if (drawing) this.state.local.drafts.value = drafts;
	}

	closeLabel() {
		if (!this.label) return;
		this.label = null;
		this.labelEditor?.close();
		this.dropShape();
	}

	createDrawing(kind: Drawing["kind"], points: number[], look: Look & { text?: string } = presetStyle(this.drawChoices, this.gridMath().grid)) {
		const id = uuid();
		const attributes = { kind, points, ...look };
		const z = Math.max(0, ...Object.values(this.state.view.peek().drawings ?? {}).map((drawing: { z?: number }) => drawing.z ?? 0)) + 1;
		const step = this.step({ type: "create", collection: "drawings", id, record: { id, ...attributes, z } });
		this.send(
			{
				method: "POST",
				url: this.drawingsUrlValue,
				body: { id, map_drawing: attributes },
				ops: [{ op: "upsert", collection: "drawings", id, attrs: { ...attributes, z } }],
			},
			step,
		);
		return id;
	}

	updateDrawing(id: string, attributes: DrawingWrite, options: WriteOptions = {}) {
		const drawing = this.state.view.peek().drawings?.[id];
		const step = drawing && this.step(updateChange("drawings", drawing, attributes, options.gesture), options);
		return this.send(
			{
				method: "PATCH",
				url: `${this.drawingsUrlValue}/${id}`,
				body: { map_drawing: attributes },
				ops: [{ op: "update", collection: "drawings", id, attrs: attributes }],
			},
			step,
			options,
		);
	}

	// A ledge's teeth stand on the right of the way its points run, so its
	// other side is the points in the other order.
	flipDrawing(id: string) {
		this.flushMove();
		const drawing = this.state.view.peek().drawings?.[id] as Drawing | undefined;
		if (drawing) this.updateDrawing(id, { points: flipPoints(drawing.points) }, { gesture: "flip" });
	}

	removeDrawing(id: string) {
		const drawing = this.state.view.peek().drawings?.[id];
		const step = drawing && this.step({ type: "remove", collection: "drawings", id, record: drawing });
		this.send({ method: "DELETE", url: `${this.drawingsUrlValue}/${id}`, ops: [{ op: "remove", collection: "drawings", id }] }, step);
	}

	// The Notas panel's island reads and drives the board through the bridge, the master's own board only. A note removed
	// elsewhere (another tab, a resync, a refused create) leaves the selection.
	startNotes() {
		const { state } = this;
		state.local.notesHidden.value = notesHiddenStored();
		notesBridge.value = {
			notes: computed(() => (state.view.value.notes ?? {}) as Record<string, LiveNote>),
			selected: state.local.note,
			armed: state.local.noteTool,
			arm: () => this.setNotes(!this.noteArmed),
			hidden: state.local.notesHidden,
			toggleHidden: () => this.hideNotes(!state.local.notesHidden.peek()),
			select: (id) => this.focusNote(id),
		};
		this.disposeNoteSelection = effect(() => {
			const id = state.local.note.value;
			if (id && !state.view.value.notes?.[id]) state.local.note.value = null;
		});
	}

	// Hiding puts the note tool down and drops a mark held now, so no note is placed or moved unseen.
	hideNotes(on: boolean) {
		if (on) {
			this.setNotes(false);
			this.renderer?.cancelGesture?.();
		}
		this.state.local.notesHidden.value = on;
		storeNotesHidden(on);
	}

	get notes(): LiveNote[] {
		return Object.values(this.state.view.peek().notes ?? {}) as LiveNote[];
	}

	// The master's note tool: the next tap on the map leaves a note there. It puts every other tool down, and each of them puts it
	// down. It does not arm at the limit.
	armNotes() {
		if (!this.manageValue || this.tableValue || this.noteArmed || this.notes.length >= NOTE_LIMIT) return;
		this.disarm();
		this.armBrush(null);
		this.armEdge(null);
		this.setDraw(false);
		this.setFog(false);
		this.setRuler(false);
		this.select(null);
		this.selectNote(null);
		this.hideNotes(false);
		this.noteArmed = true;
		this.state.local.noteTool.value = true;
		this.renderer?.setTool("note");
		this.dispatch("notes", { detail: { on: true } });
	}

	// The tray's Tabuleiro panel is open: the grid shows on the uncarved floor too.
	showFloorGrid(on: boolean) {
		this.floorGrid = on;
		this.renderer?.setFloorGrid(on);
	}

	setNotes(on: boolean) {
		if (on) return this.armNotes();
		if (!this.noteArmed) return;
		this.noteArmed = false;
		this.state.local.noteTool.value = false;
		this.renderer?.setTool("move");
		this.dispatch("notes", { detail: { on: false } });
	}

	selectNote(id: string | null) {
		if (id) this.select(null);
		this.state.local.note.value = id;
	}

	// A row of the panel: the map centred on its mark, then selected, so the card that opens reveals the mark if it covers it.
	focusNote(id: string) {
		const note = this.state.view.peek().notes?.[id];
		if (!note) return;
		this.renderer?.centreAt(note);
		this.selectNote(id);
	}

	// The tap lands where it fell, numbered after the highest number in use, and the new note is selected with the tool put down.
	createNote(point: Point) {
		if (this.notes.length >= NOTE_LIMIT) return this.setNotes(false);
		const id = uuid();
		const [x, y] = this.onScene(point);
		const attributes = { x, y, mark: nextMark(this.notes), text: "", tags: "" };
		const step = this.step({ type: "create", collection: "notes", id, record: { id, ...attributes } });
		batch(() => {
			this.send(
				{ method: "POST", url: this.notesUrlValue, body: { id, map_note: attributes }, ops: [{ op: "upsert", collection: "notes", id, attrs: attributes }] },
				step,
			);
			this.setNotes(false);
			this.selectNote(id);
		});
		return id;
	}

	moveNote(id: string, point: Point) {
		const note = this.state.view.peek().notes?.[id];
		if (!note) return;
		const [x, y] = this.onScene(point);
		if (note.x !== x || note.y !== y) this.updateNote(id, { x, y });
	}

	updateNote(id: string, attributes: Partial<Pick<LiveNote, "x" | "y" | "mark" | "text" | "tags">>, options: WriteOptions = {}) {
		const note = this.state.view.peek().notes?.[id];
		const step = note && this.step(updateChange("notes", note, attributes), options);
		return this.send(
			{
				method: "PATCH",
				url: `${this.notesUrlValue}/${id}`,
				body: { map_note: attributes },
				ops: [{ op: "update", collection: "notes", id, attrs: attributes }],
			},
			step,
			options,
		);
	}

	removeNote(id: string) {
		const note = this.state.view.peek().notes?.[id];
		const step = note && this.step({ type: "remove", collection: "notes", id, record: note });
		batch(() => {
			if (this.state.local.note.peek() === id) this.selectNote(null);
			this.send({ method: "DELETE", url: `${this.notesUrlValue}/${id}`, ops: [{ op: "remove", collection: "notes", id }] }, step);
		});
	}

	disarm() {
		if (!this.placing) return;
		this.placing = null;
		this.dispatch("placed");
	}

	// Iniciar combate: the tool in hand goes down first, or the ruler would take the taps that tick, and a key step still waiting is
	// written before ticking begins.
	startTicks(eligible: string[]) {
		this.settleGestures();
		this.disarm();
		this.armBrush(null);
		this.armEdge(null);
		this.setDraw(false);
		this.setFog(false);
		this.setNotes(false);
		this.setRuler(false);
		this.select(null);
		this.state.local.ticks.value = initialTicks(eligible);
	}

	// The master's + Monstro: hidden on the free cell nearest the monsters already there, or the view's centre when there are none.
	placeHidden(subject: TraySubject) {
		const { tokens, scene } = this.state.view.peek();
		const pieces = Object.values(tokens ?? {}) as LiveToken[];
		const { cx, cy } = this.renderer.centre();
		const target = centroid(pieces.filter((token) => token.kind === "monster")) ?? { x: cx, y: cy };
		return this.placeToken(subject, freeSpot(this.gridMath(), target, pieces, scene), { hidden: true });
	}

	placeToken(subject: TraySubject, point, { hidden = false } = {}) {
		this.disarm();
		const view = this.state.view.peek();
		const at = snapPoint(this.gridMath(), point, 1);
		const id = uuid();
		const attributes: Record<string, unknown> = {
			kind: subject.kind,
			label: subject.label ?? null,
			x: at.x,
			y: at.y,
			size: 1,
			ring_color: subject.ring_color ?? null,
		};
		for (const key of SUBJECT_KEYS) if (subject[key]) attributes[key] = subject[key];
		if (hidden) attributes.hidden = true;
		const z = Math.max(0, ...Object.values(view.tokens ?? {}).map((token: { z?: number }) => token.z ?? 0)) + 1;
		const record = {
			...attributes,
			rotation: 0,
			z,
			hidden,
			locked: false,
			snap: true,
			// An unnamed place's default name is empty (MapToken#default_place_name).
			name: subject.label || (this.unlabeledTitlesValue[subject.prefab] ? null : subject.name),
			image_url: subject.image_url ?? null,
			pv: subject.pv ?? null,
			max_pv: subject.max_pv ?? null,
			owner_account_id: subject.owner_account_id ?? null,
			sheet_url: subject.sheet_url ?? null,
		};
		const step = this.step({ type: "create", collection: "tokens", id, record: { id, ...record } });
		this.send(
			{ method: "POST", url: this.tokensUrlValue, body: { id, map_token: attributes }, ops: [{ op: "upsert", collection: "tokens", id, attrs: record }] },
			step,
		);
		return id;
	}

	// Ctrl/Cmd+C: the selected pieces, kept in memory. A text selected on the page keeps its own copy.
	copySelected() {
		if (globalThis.getSelection?.()?.toString()) return false;
		this.flushStep();
		const tokens = this.state.view.peek().tokens ?? {};
		// In stacking order, so the copies keep the sources' relative z.
		const pieces = this.state.local.selection
			.peek()
			.flatMap((id) => (tokens[id] ? [{ ...tokens[id] }] : []))
			.sort(byBandThenZ);
		if (!pieces.length) return false;
		this.clipboard = { pieces, pastes: 0 };
		return true;
	}

	// Ctrl/Cmd+V: the copied group, centred under the mouse (one cell further each time without one on the board), each piece by its
	// own snap rule, above everything and as one step of the history. A group that would leave the scene is refused whole.
	pasteCopied() {
		const { clipboard } = this;
		if (!clipboard) return false;
		this.disarm();
		this.flushStep();
		const view = this.state.view.peek();
		const math = this.gridMath();
		const pointer = this.renderer.pointerAt();
		const middle = centroid(clipboard.pieces) as { x: number; y: number };
		const pasted = ++clipboard.pastes;
		const shift = pointer ? { x: pointer.x - middle.x, y: pointer.y - middle.y } : { x: pasted * math.grid.cell_w, y: pasted * math.grid.cell_h };
		const copies = Object.fromEntries(clipboard.pieces.map((piece) => [uuid(), piece]));
		const at = Object.fromEntries(Object.entries(copies).map(([id, piece]) => [id, { x: piece.x + shift.x, y: piece.y + shift.y }]));
		const moves = groupDropTargets(math, copies, at, view.scene);
		if (!moves) {
			this.toast(this.labelsValue.refused.outside);
			return true;
		}
		let z = Math.max(0, ...Object.values(view.tokens ?? {}).map((token: { z?: number }) => token.z ?? 0));
		batch(() =>
			this.group(() => {
				for (const { id, x, y } of moves) {
					const record = { ...copies[id], id, x, y, z: Math.min(++z, Z_LIMIT) };
					const step = this.step({ type: "create", collection: "tokens", id, record });
					this.send(
						{
							method: "POST",
							url: this.tokensUrlValue,
							body: { id, map_token: createBody("tokens", record) },
							ops: [{ op: "upsert", collection: "tokens", id, attrs: record }],
						},
						step,
					);
				}
			}),
		);
		this.setSelection(moves.map(({ id }) => id));
		return true;
	}

	// Any move of a token replaces the key steps still waiting to be written,
	// and takes a turn still waiting along.
	moveToken(id, { x, y }) {
		// Tick mode takes taps only: a token dragged then glides back.
		if (this.state.local.ticks?.peek()) return this.dropStep(id);
		const token = this.state.view.peek().tokens?.[id];
		const { step } = this.state.local.drafts.peek();
		batch(() => {
			this.dropStep(id);
			this.dropGroupStep(id);
			if (!token) return;
			const changes: { x?: number; y?: number; rotation?: number } = {};
			if (token.x !== x || token.y !== y) Object.assign(changes, { x, y });
			if (step?.id === id && step.rotation !== undefined && step.rotation !== (token.rotation ?? 0)) changes.rotation = step.rotation;
			if (Object.keys(changes).length) this.updateToken(id, changes);
		});
	}

	// A group moved by a drag or the keys, one step of the history. Each piece goes as moveToken would write it; a piece gone or hidden
	// since is left out.
	moveTokens(moves: { id: string; x: number; y: number }[]) {
		if (this.state.local.ticks?.peek()) return;
		batch(() =>
			this.group(() => {
				for (const { id, x, y } of moves) this.moveToken(id, { x, y });
			}),
		);
	}

	// The same attributes on every piece they would change, one step of the history.
	updateTokens(ids: string[], attributes: Record<string, unknown>) {
		const tokens = this.state.view.peek().tokens ?? {};
		const changing = ids.filter((id) => {
			const change = tokens[id] && (updateChange("tokens", tokens[id], attributes) as Extract<Change, { type: "update" }>);
			return change && Object.keys(change.after).some((key) => change.before[key] !== change.after[key]);
		});
		this.group(() => {
			for (const id of changing) this.updateToken(id, attributes);
		});
	}

	removeTokens(ids: string[]) {
		if (ids.length === 1) return this.removeToken(ids[0]);
		this.select(null);
		this.group(() => {
			for (const id of ids) this.removeToken(id);
		});
	}

	// `shown` joins the optimistic view only, for what the server derives.
	updateToken(id, attributes, shown = {}, options: WriteOptions = {}) {
		const token = this.state.view.peek().tokens?.[id];
		const step = token && this.step(updateChange("tokens", token, attributes, options.gesture), options);
		return this.send(
			{
				method: "PATCH",
				url: `${this.tokensUrlValue}/${id}`,
				body: { map_token: attributes },
				ops: [{ op: "update", collection: "tokens", id, attrs: { ...attributes, ...shown } }],
			},
			step,
			options,
		);
	}

	// An empty label falls back to the subject's name, which the echo brings.
	// The renames of one name field's focus (`session`) are one step.
	renameToken(id, label, session?: string) {
		this.updateToken(id, { label }, label ? { name: label } : {}, session ? { merge: `rename:${id}:${session}` } : {});
	}

	// A token of a new size or span moves to where its new shape snaps, a
	// corner or a centre on each axis; one whose own snap is off keeps its
	// position.
	resizeToken(id, size) {
		const token = this.state.view.peek().tokens?.[id];
		if (!token) return;
		const at = snapTarget(this.gridMath(), token, token, size);
		this.updateToken(id, { size, x: at.x, y: at.y });
	}

	stretchToken(id: string, span: number | null) {
		const token = this.state.view.peek().tokens?.[id];
		if (!token) return;
		const at = snapTarget(this.gridMath(), { ...token, span }, token);
		this.updateToken(id, { span, x: at.x, y: at.y });
	}

	// A new width with the length kept is a new size and span together.
	reshapeToken(id: string, size: number, span: number | null) {
		const token = this.state.view.peek().tokens?.[id];
		if (!token) return;
		const at = snapTarget(this.gridMath(), { ...token, span }, token, size);
		this.updateToken(id, { size, span, x: at.x, y: at.y });
	}

	removeToken(id) {
		const token = this.state.view.peek().tokens?.[id];
		const step = token && this.step({ type: "remove", collection: "tokens", id, record: token });
		this.select(null);
		this.send({ method: "DELETE", url: `${this.tokensUrlValue}/${id}`, ops: [{ op: "remove", collection: "tokens", id }] }, step);
	}

	// The table view writes as the players: its answer carries their diff,
	// which is the one its stream settles against.
	// `local` replaces keys of the op only, such as a whole setting where the
	// write names the keys it changes.
	// A terrain stroke, a fog gesture, an edge or the grid shown or hidden is a step; a setting is not.
	updateScene(attributes, local = {}, options: WriteOptions = {}) {
		const scene = this.state.view.peek().scene;
		const { id } = scene;
		// G, its undo and its redo all write the visibility alone; an open Configurações panel follows, or its draft would bring the old one back.
		const keys = attributes.grid ? Object.keys(attributes.grid) : [];
		if (keys.length === 1 && keys[0] === "visible") {
			const panel = this.application.getControllerForElementAndIdentifier(this.element, "map-calibration") as
				| (Controller & { gridShown(visible: boolean): void })
				| null;
			panel?.gridShown(attributes.grid.visible);
		}
		return this.send(
			{
				method: "PATCH",
				url: `${this.scenesUrlValue}/${id}.json${this.tableValue ? "?view=players" : ""}`,
				body: { map_scene: attributes },
				ops: [{ op: "update", collection: "scene", id, attrs: { ...attributes, ...local } }],
			},
			this.step(sceneChange(scene, attributes), options),
			options,
		);
	}

	undo() {
		return this.travel("undo");
	}

	redo() {
		return this.travel("redo");
	}

	// One press takes one step back, or forward; a step nothing of which still
	// holds is dropped, silently, and the same press goes on to the next.
	travel(direction: Direction) {
		const { history } = this;
		if (!history) return false;
		this.settleGestures();
		const undo = direction === "undo";
		const [next, take] = undo ? [history.nextUndo, history.takeUndo] : [history.nextRedo, history.takeRedo];
		if (this.queuedTravel) return false;
		// The removal this would take back may answer the combatant it took with it, which its create must send back.
		const top = next.peek();
		const waiting = top && idsOf(top).find((id) => this.removals?.has(id));
		if (waiting) {
			this.queuedTravel = { direction, mutationId: waiting };
			return true;
		}
		while (next.peek() && !this.waits(next.peek(), this.state.view.peek())) {
			const entry = take();
			const reversed = reverseAll(entry.change, this.state.view.peek(), direction, this.viewer);
			if (!reversed) continue;
			// A group is taken back by one write per piece, and goes onto the other stack as one step.
			const written: string[] = [];
			for (const { write, change } of reversed.steps) {
				const mutationId = this.apply(write);
				if (!mutationId) continue;
				this.track(mutationId, change);
				if (write.type === "remove" && write.collection === "tokens" && "record" in change) this.awaitCombat(mutationId, change);
				written.push(mutationId);
			}
			if (written.length) {
				// A group that lost pieces on the way is named for those left.
				const shrunk = entry.change.type === "group" && (reversed.change.type !== "group" || reversed.change.changes.length !== entry.change.changes.length);
				const { mutationIds: _written, ...base } = entry;
				const next = { ...base, mutationId: written[0], change: reversed.change, ...(shrunk && { label: this.describe(reversed.change) }) };
				(undo ? history.pushRedo : history.pushUndo)(reversed.change.type === "group" ? { ...next, mutationIds: written } : next);
			}
			return true;
		}
		return false;
	}

	// A player's step waits, kept, while their piece may not move (locked, or
	// movement frozen); only a change made since drops it.
	waits(entry: Entry<Change>, view) {
		const { change } = entry;
		if (this.manageValue || change.type !== "update" || change.collection !== "tokens") return false;
		const token = view.tokens?.[change.id];
		return Boolean(token) && !canMove(token, this.viewer, view.scene);
	}

	// A drag, a shape, a stroke or a handle drag in progress never was a step,
	// and ends first; a key step or a nudge still waiting is then written, so
	// it is the step taken back.
	settleGestures() {
		this.renderer?.cancelGesture?.();
		this.renderer?.cancelTransform?.();
		this.dropTransform();
		this.dropReshape();
		if (this.shape) this.dropShape();
		this.flushStep();
		this.flushMove();
	}

	apply(write: Write) {
		const options = { record: false, quiet: true };
		if (write.type === "scene") return this.updateScene(write.attrs, write.local, options);
		if (write.type === "update") {
			const { id, attrs } = write;
			if (write.collection === "drawings") return this.updateDrawing(id, attrs as DrawingWrite, options);
			if (write.collection === "notes") return this.updateNote(id, attrs, options);
			return this.updateToken(id, attrs, write.shown ?? {}, options);
		}
		const [url, key] = {
			tokens: [this.tokensUrlValue, "map_token"],
			drawings: [this.drawingsUrlValue, "map_drawing"],
			notes: [this.notesUrlValue, "map_note"],
		}[write.collection];
		const { collection, id } = write;
		if (write.type === "remove") return this.send({ method: "DELETE", url: `${url}/${id}`, ops: [{ op: "remove", collection, id }] }, null, options);
		const body = { id, [key]: write.body, ...(write.combat && { combat: write.combat }) };
		return this.send({ method: "POST", url, body, ops: [{ op: "upsert", collection, id, attrs: write.record }] }, null, options);
	}

	// The step a write makes, taken from the view before it applies.
	step(change: Change | null, { record = true, merge }: WriteOptions = {}) {
		if (!this.history || !record || !change) return null;
		return { label: this.describe(change), change, merge };
	}

	send(request: Request, step?: { label: string; change: Change; merge?: string } | null, { quiet = false }: WriteOptions = {}) {
		const mutationId = this.outbox.enqueue(quiet ? { ...request, quiet } : request);
		if (step && mutationId && this.history) {
			this.track(mutationId, step.change);
			if (step.change.type === "remove" && step.change.collection === "tokens") this.awaitCombat(mutationId, step.change);
			if (this.grouped) this.grouped.push({ mutationId, step });
			else this.history.record({ mutationId, ...step }, mergeChanges);
		}
		return mutationId;
	}

	// The writes `write` makes are one step of the history ("Mover 3 peças"): undone, redone and, when any is refused, forgotten together.
	group(write: () => void) {
		if (this.grouped) return write();
		const made: NonNullable<typeof this.grouped> = [];
		this.grouped = made;
		try {
			write();
		} finally {
			this.grouped = null;
		}
		if (made.length === 1) this.history?.record({ mutationId: made[0].mutationId, ...made[0].step }, mergeChanges);
		else if (made.length) {
			const change: Change = { type: "group", changes: made.map(({ step }) => step.change) };
			this.history?.record({ mutationId: made[0].mutationId, mutationIds: made.map(({ mutationId }) => mutationId), label: this.describe(change), change });
		}
	}

	describe(change: Change) {
		const { actions } = this.labelsValue.history;
		if (change.type === "group") return (actions[actionOf(change)] ?? "").replace("%{count}", String(change.changes.length));
		const tokens = "collection" in change && change.collection === "tokens";
		const token = !tokens ? null : change.type === "update" ? this.state.view.peek().tokens?.[change.id] : change.record;
		const name = token ? this.titleOf(token) : "";
		return (actions[actionOf(change)] ?? "").replace("%{name}", () => name);
	}

	// The table shows, on any screen, the stretch of map the master sees now.
	sendTableView() {
		const { table_view: current, active } = this.state.view.peek().scene;
		if (!this.renderer || !active) return;
		const view = tableViewOf(this.state.local.viewport.peek(), this.renderer.size(), this.chromeInsets());
		this.updateScene({ table_view: { ...current, ...view } });
		this.toast(this.labelsValue.tableSent, "notice");
	}

	openTable({ currentTarget }) {
		openTable(currentTarget.href, this.campaignIdValue);
	}

	toggleFrozen() {
		this.updateScene({ movement_frozen: !this.state.view.peek().scene.movement_frozen });
	}

	activate() {
		const id = this.state.view.peek().scene.id;
		this.outbox.enqueue({
			method: "PATCH",
			url: `${this.scenesUrlValue}/${id}/ativar.json`,
			ops: [{ op: "update", collection: "scene", id, attrs: { active: true } }],
		});
	}

	get viewportKey() {
		return `maps-viewport:${this.state.view.peek().scene?.id}`;
	}

	restoreViewport() {
		if (this.tableValue) return;
		try {
			const saved = JSON.parse(localStorage.getItem(this.viewportKey) ?? "null");
			if (saved) this.renderer.restoreView(saved);
		} catch {}
	}

	rememberViewport() {
		clearTimeout(this.viewportTimer);
		this.viewportTimer = setTimeout(() => {
			if (!this.renderer || this.renderer.autoFit || this.tableValue) return;
			try {
				localStorage.setItem(this.viewportKey, JSON.stringify(this.renderer.centre()));
			} catch {}
		}, VIEWPORT_SAVE_MS);
	}

	toggleRuler() {
		this.setRuler(this.renderer?.tool !== "ruler");
	}

	setRuler(on) {
		if (on) {
			this.armBrush(null);
			this.armEdge(null);
			this.setDraw(false);
			this.setFog(false);
			this.setNotes(false);
		}
		this.renderer?.setTool(on ? "ruler" : this.brush ? "paint" : this.edgeKind ? "edge" : this.drawArmed ? "draw" : this.noteArmed ? "note" : "move");
		for (const button of this.rulerTargets) button.setAttribute("aria-pressed", String(on));
	}

	pickShape({ params: { shape } }: { params: { shape: string } }) {
		this.rulerShape = knownShape(shape);
		try {
			localStorage.setItem(RULER_SHAPE, this.rulerShape);
		} catch {}
		this.applyRulerKind();
	}

	// A width that will not do keeps the last good one while it is typed, and goes back to it on leaving the field.
	setWidth() {
		const text = this.rulerWidthTargets[0]?.value ?? "";
		const width = parseWidth(text);
		if (width === null && text.trim() !== "") return;
		this.rulerWidth = width;
		this.applyRulerKind();
	}

	settleWidth() {
		const field = this.rulerWidthTargets[0];
		if (!field) return;
		field.value =
			this.rulerWidth === null
				? ""
				: new Intl.NumberFormat(document.documentElement.lang || undefined, { maximumFractionDigits: 2, useGrouping: false }).format(this.rulerWidth);
	}

	// The shape last used in this browser, and the buttons and the width field that show it.
	applyRulerKind() {
		for (const button of this.rulerShapeTargets) button.setAttribute("aria-pressed", String(button.dataset.mapBoardShapeParam === this.rulerShape));
		for (const field of this.rulerWidthFieldTargets) field.hidden = this.rulerShape !== "cone";
		this.renderer?.setRulerKind({ shape: this.rulerShape, width: this.rulerWidth });
	}

	zoomIn() {
		this.renderer?.zoomIn();
	}

	zoomOut() {
		this.renderer?.zoomOut();
	}

	resetZoom() {
		this.renderer?.zoomReset();
	}

	showZoom(zoom: number) {
		for (const level of this.zoomLevelTargets) level.textContent = `${Math.round(zoom * 100)}%`;
	}

	fit() {
		this.renderer?.fit();
		try {
			localStorage.removeItem(this.viewportKey);
		} catch {}
	}

	measureLabel({ cells, amount, unit, kind, shape, width }) {
		const { measure } = this.labelsValue;
		const names = measure[kind] ?? measure.square;
		const number = new Intl.NumberFormat(document.documentElement.lang || undefined, { maximumFractionDigits: 1 });
		return (shape ? measure[shape] : measure.format)
			.replace("%{cells}", (cells === 1 ? names.one : names.other).replace("%{count}", number.format(cells)))
			.replace("%{distance}", number.format(amount))
			.replace("%{width}", number.format(width))
			.replaceAll("%{unit}", unit);
	}

	showMeasure(measurement) {
		this.measureTarget.textContent = measurement?.text ?? "";
		this.measureTarget.hidden = !measurement;
	}

	toast(message, variant = "alert", href = "") {
		const template = this.toastTemplateTargets.find((element) => element.dataset.variant === variant) ?? this.toastTemplateTarget;
		const toast = template.content.firstElementChild.cloneNode(true) as HTMLElement;
		toast.querySelector("[data-toast-message]").textContent = message;
		const link = toast.querySelector<HTMLAnchorElement>("[data-toast-link]");
		if (href && link) {
			link.href = href;
			link.addEventListener("click", () => toast.remove());
		} else link?.remove();
		this.present(toast, href && link ? LINK_TOAST_MS : TOAST_MS);
	}

	// Toasts keep clear of a panel docked on the right.
	present(toast, ms = TOAST_MS) {
		if (!this.tableValue) this.toastsTarget.style.marginRight = `${this.chromeInsets().right}px`;
		this.toastsTarget.append(toast);
		while (this.toastsTarget.childElementCount > TOASTS_KEPT) this.toastsTarget.firstElementChild.remove();
		this.toastTimers ??= new Set();
		const expire = (after) => {
			const timer = setTimeout(() => {
				this.toastTimers.delete(timer);
				if (ms > TOAST_MS && this.toastHeld(toast)) expire(TOAST_MS);
				else toast.remove();
			}, after);
			this.toastTimers.add(timer);
		};
		expire(ms);
	}

	// A tap leaves :hover and focus behind on a touch screen, so only a real
	// hover or the keyboard's focus holds a toast.
	toastHeld(toast: HTMLElement) {
		const focused = document.activeElement;
		if (focused && toast.contains(focused) && focused.matches(":focus-visible")) return true;
		return matchMedia("(hover: hover)").matches && toast.matches(":hover");
	}

	get tokenMenu() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-token-menu") as MapTokenMenu | null;
	}

	get drawingMenu() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-drawing-menu") as MapDrawingMenu | null;
	}

	get edgeMenu() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-edge-menu") as MapEdgeMenu | null;
	}

	get dice() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-dice") as MapDice | null;
	}

	get labelEditor() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-label") as MapLabel | null;
	}

	// A read-only handle for Playwright and the probe: canvas has no DOM to query.
	// Screen positions are client coordinates, ready for page.mouse.
	exposeForTests() {
		if (import.meta.env.MODE === "production") return;
		const { state, renderer, element } = this;
		window.odMaps = Object.freeze({
			element,
			sceneId: state.view.peek().scene?.id,
			view: () => structuredClone(state.view.peek()),
			version: () => state.version.peek(),
			pending: () => state.pending.peek().length,
			selection: () => [...state.local.selection.peek()],
			multi: () => state.local.multi.peek(),
			ticks: () => structuredClone(state.local.ticks.peek()),
			combat: () => structuredClone(state.local.combat.peek()),
			tokenOverlay: (id) => renderer.tokenOverlay(id),
			viewport: () => renderer.viewport(),
			insets: () => this.chromeInsets(),
			tool: () => renderer.tool,
			measurement: () => renderer.measurement(),
			cellToScreen: (col, row) => renderer.cellToScreen(col, row),
			screenToCell: (x, y) => renderer.screenToCell(x, y),
			imageToScreen: (x, y) => renderer.imageToClient({ x, y }),
			calibrationGuide: () => renderer.calibrationGuide(),
			draftGrid: () => structuredClone(state.local.drafts.peek().grid ?? null),
			terrain: () => [...(state.local.drafts.peek().terrain?.rows ?? state.view.peek().scene?.terrain?.rows ?? [])],
			cuts: () => [...(state.local.drafts.peek().terrain?.cuts ?? state.view.peek().scene?.terrain?.cuts ?? [])],
			// A terrain draft's absent borders mean none and a draft of rows alone keeps the scene's, as the renderer draws them.
			borders: () => {
				const [draft, stored] = [state.local.drafts.peek().terrain, state.view.peek().scene?.terrain];
				return [...((draft && !Array.isArray(draft) ? draft : stored)?.borders ?? [])];
			},
			brush: () => this.brush ?? null,
			walls: () => renderer.walls(),
			edges: () => renderer.edges(),
			drawings: () => structuredClone(Object.values(state.view.peek().drawings ?? {}) as Drawing[]),
			notes: () => structuredClone(Object.values(state.view.peek().notes ?? {})),
			noteSelection: () => state.local.note.peek(),
			noteArmed: () => state.local.noteTool.peek(),
			noteMarks: () => renderer.noteMarks(),
			drawDraft: () => structuredClone(state.local.drafts.peek().drawing ?? null),
			drawSelection: () => structuredClone(state.local.drafts.peek().move ?? null),
			drawingMenu: () => state.local.drawingMenu.peek(),
			reshapeDraft: () => structuredClone(state.local.drafts.peek().reshape ?? null),
			drawingBadgeScreenPosition: () => renderer.drawingBadgeScreenPosition(),
			tokenScreenPosition: (id) => renderer.tokenScreenPosition(id),
			pings: () => renderer.pings(),
			rulers: () => renderer.rulers(),
			participants: () => structuredClone(state.view.peek().participants ?? {}),
			presence: () => [...(this.presence?.present.peek() ?? [])],
			badgeScreenPosition: () => renderer.badgeScreenPosition(),
			handles: () => state.local.handles.peek(),
			handleAnchors: () => renderer.handleAnchors(),
			transformDraft: () => structuredClone(state.local.drafts.peek().transform ?? null),
		});
	}
}

function inRadioGroup(element) {
	return element?.type === "radio" || Boolean(element?.closest?.('[role="radiogroup"], [role="tablist"]'));
}

function rectOf(from: Point, to: Point) {
	return { x: from.x, y: from.y, width: to.x - from.x, height: to.y - from.y };
}
